import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, useReducer } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CSpinner,
  CFormSelect,
  CDropdown, 
  CDropdownItem, 
  CDropdownMenu, 
  CDropdownToggle,
  CFormInput,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilArrowLeft, cilInfo } from "@coreui/icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import CustomVideoPlayer from "../../components/CustomVideoPlayer/CustomVideoPlayer";
import "./ImuViewerPage.css";
import JSZip from "jszip"

const VIDEO_FPS = 24.0;

const DIVIDERS = [
  1,5,10,25,50,100,250,500,
  1000,5000,10000,30000,
  60000,300000,600000,1800000,3600000,
  7200000,14400000,28800000
]
const WIDTH_LETTERS = [
  12,12,12,12,12,12,12,12, //HH:MM:SS.mmm
  8,8,8,8, //HH:MM:SS
  5,5,5,5,5, //HH:MM
  5,5,5 //HH:00 
]

// Быстрый бинарный поиск вместо тяжелого findIndex
const binarySearchTime = (points, targetTime) => {
  let low = 0;
  let high = points.length - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (points[mid].t < targetTime) {
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return low;
};

const MIN_LEVEL_POINTS = 600;

const SENSOR_DEFS = [
  {
    key: "accel",
    axes: [
      { label: "x", name: "Аксель: X (Продольное ускорение)", color: "#B84F4F" },
      { label: "y", name: "Аксель: Y (Поперечное ускорение)", color: "#4FB86D" },
      { label: "z", name: "Аксель: Z (Вертикальное ускорение)", color: "#5058B8" },
    ],
  },
  {
    key: "gyro",
    axes: [
      { label: "x", name: "Гироскоп: X (Угл. скорость Roll)", color: "#B84F4F" },
      { label: "y", name: "Гироскоп: Y (Угл. скорость Pitch)", color: "#4FB86D" },
      { label: "z", name: "Гироскоп: Z (Угл. скорость Yaw)", color: "#5058B8" },
    ],
  },
  {
    key: "magnet",
    axes: [
      { label: "x", name: "Магнитометр: Поворот башни (Yaw)", color: "#B84F4F" },
      { label: "y", name: "Магнитометр: Наклон вбок (Roll)", color: "#4FB86D" },
      { label: "z", name: "Магнитометр: Наклон вперед/назад (Pitch)", color: "#5058B8" },
    ],
  },
];

// Пирамида LOD на типизированных массивах.
// t общий для всех осей сенсора, значения — по одному Float32Array на ось.
// Каждый следующий уровень вдвое короче: 4 точки сворачиваются в пару (min, max).
function buildSensorPyramid(rows, axes) {
  const n = rows.length;
  const axisCount = axes.length;

  const t0 = new Float64Array(n);
  const v0 = new Array(axisCount);
  for (let a = 0; a < axisCount; a++) v0[a] = new Float32Array(n);

  for (let i = 0; i < n; i++) {
    const r = rows[i];
    t0[i] = r.t;
    for (let a = 0; a < axisCount; a++) v0[a][i] = r[axes[a].label];
  }

  const levels = [{ t: t0, v: v0 }];
  let cur = levels[0];

  while (cur.t.length > MIN_LEVEL_POINTS) {
    const srcLen = cur.t.length;
    const outLen = (srcLen >> 2) * 2;
    if (outLen < 2) break;

    const t = new Float64Array(outLen);
    const v = new Array(axisCount);
    for (let a = 0; a < axisCount; a++) v[a] = new Float32Array(outLen);

    let w = 0;
    for (let i = 3; i < srcLen; i += 4) {
      const s = i - 3;
      t[w] = cur.t[s];
      t[w + 1] = cur.t[i];
      for (let a = 0; a < axisCount; a++) {
        const src = cur.v[a];
        let mn = src[s];
        let mx = mn;
        for (let k = s + 1; k <= i; k++) {
          const val = src[k];
          if (val < mn) mn = val;
          else if (val > mx) mx = val;
        }
        v[a][w] = mn;
        v[a][w + 1] = mx;
      }
      w += 2;
      }

    cur = { t, v };
    levels.push(cur);
  }

  return levels;
}

function lowerBound(t, target) {
  let lo = 0;
  let hi = t.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (t[mid] < target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

// Времена в markups приходят в трёх видах: строка с бэкенда, объект Date из
// handleSectionCreation/loadPredictions, строка после черновика в localStorage.
// Поэтому читаем через toMs, а пишем всегда через toIso.
const toMs = (value) => new Date(value).getTime();
const toIso = (ms) => new Date(Math.round(ms)).toISOString();

// Соседство — точное равенство времён, но сравнивать надо миллисекунды:
// два разных Date не равны по ссылке, а Date не равен строке.
const areAdjacent = (left, right) => toMs(left.end_time) === toMs(right.start_time);

/**
 * Сортирует разметку и склеивает соседние сегменты одного класса в один.
 * Вызывается в конце каждой операции правки, поэтому двух соприкасающихся
 * сегментов одного класса в состоянии не бывает.
 *
 * preferredId — сегмент, который должен пережить слияние. Во время
 * перетаскивания это тот, за ручку которого держатся: если бы выжил сосед,
 * ручка исчезла бы из разметки вместе с захватом указателя и тащить стало
 * бы нечего. В остальных случаях id наследует левый из пары.
 */
function mergeAdjacentSameClass(markups, preferredId) {
  const sorted = [...markups].sort((a, b) => toMs(a.start_time) - toMs(b.start_time));
  const out = [];

  for (const current of sorted) {
    const prev = out[out.length - 1];

    if (prev && prev.class?.id == current.class?.id && areAdjacent(prev, current)) {
      const keep = preferredId != null && current.id == preferredId ? current : prev;
      out[out.length - 1] = {
        ...keep,
        start_time: prev.start_time,
        end_time: current.end_time,
      };
      continue;
    }

    out.push(current);
  }

  return out;
}

/**
 * Вписывает segment в набор, разрешая перекрытия:
 *   - полностью накрытый существующий сегмент удаляется;
 *   - задетый с краю обрезается;
 *   - существующий, внутрь которого попал новый, разрезается на два;
 *   - вырожденные (end <= start) выбрасываются.
 * Сегмент с тем же id заменяется, поэтому функция годится и для рисования
 * нового, и для изменения размеров существующего.
 * Касание край в край пересечением не считается — цепочка классов уцелеет.
 */
function resolveOverlaps(markups, segment, makeId) {
  const s = toMs(segment.start_time);
  const e = toMs(segment.end_time);
  const out = [];

  for (const m of markups) {
    if (m.id == segment.id) continue;

    const ms = toMs(m.start_time);
    const me = toMs(m.end_time);

    if (!(me > ms)) continue;                          // схлопнулся или вывернулся
    if (me <= s || ms >= e) { out.push(m); continue; } // не пересекается
    if (ms >= s && me <= e) continue;                  // полностью накрыт

    if (ms < s && me > e) {                            // новый целиком внутри — режем на два
      out.push({ ...m, start_time: toIso(ms), end_time: toIso(s) });
      out.push({ ...m, id: makeId(), start_time: toIso(e), end_time: toIso(me) });
      continue;
    }

    if (ms < s) out.push({ ...m, start_time: toIso(ms), end_time: toIso(s) });
    else out.push({ ...m, start_time: toIso(e), end_time: toIso(me) });
  }

  out.push({ ...segment, start_time: toIso(s), end_time: toIso(e) });
  return mergeAdjacentSameClass(out, segment.id);
}

/**
 * Двигает край сегмента.
 * Если по этой стороне есть сосед, общая граница едет у обоих сразу.
 * Всё, что граница переезжает, поглощается подряд.
 *
 * base — снимок markups на момент pointerdown: каждое движение считается от
 * него заново, поэтому обратное движение мыши возвращает съеденное.
 */
function resizeSegment(base, id, side, boundaryMs, minMs, makeId) {
  const seg = base.find((m) => m.id == id);
  if (!seg) return base;

  const s = toMs(seg.start_time);
  const e = toMs(seg.end_time);

  // Перетаскиваемый сегмент не даём схлопнуть — иначе исчезнет то, что держим.
  const start = side === "left" ? Math.min(boundaryMs, e - minMs) : s;
  const end = side === "left" ? e : Math.max(boundaryMs, s + minMs);

  const moved = { ...seg, start_time: toIso(start), end_time: toIso(end) };

  const neighbor = side === "left"
    ? base.find((m) => m.id != seg.id && areAdjacent(m, seg))
    : base.find((m) => m.id != seg.id && areAdjacent(seg, m));

  let rest = base.filter((m) => m.id != seg.id);
  if (neighbor) {
    rest = rest.map((m) =>
      m.id != neighbor.id
        ? m
        : side === "left"
          ? { ...m, end_time: moved.start_time }
          : { ...m, start_time: moved.end_time }
    );
  }

  return resolveOverlaps(rest, moved, makeId);
}

/**
 * Удаляет сегмент. Если он зажат между двумя соседями, они сходятся
 * в его центр — дыры в цепочке не остаётся. С одним соседом или без
 * соседей просто удаляем: цепочка тогда лишь укорачивается с края.
 */
function deleteSegment(markups, id, snapMode = "middle") {
  const seg = markups.find((m) => m.id == id);
  if (!seg) return markups;

  const left = markups.find((m) => m.id != id && areAdjacent(m, seg));
  const right = markups.find((m) => m.id != id && areAdjacent(seg, m));
  const rest = markups.filter((m) => m.id != id);

  // "none" — соседей не трогаем вовсе: на месте удалённого остаётся пробел.
  // Соседей у края цепочки двигать тоже некуда.
  if (!left || !right || snapMode === "none") return mergeAdjacentSameClass(rest);

  // Куда сходятся соседи на месте удалённого сегмента:
  //   "left"   — граница встаёт в начало удалённого: правый растёт влево;
  //   "middle" — в середину: оба сдвигаются навстречу;
  //   "right"  — в конец удалённого: левый растёт вправо.
  const start = toMs(seg.start_time);
  const end = toMs(seg.end_time);
  const boundary = toIso(
    snapMode === "left" ? start : snapMode === "right" ? end : (start + end) / 2
  );

  // Если соседи одного класса, слияние тут же превратит их в один сегмент.
  return mergeAdjacentSameClass(
    rest.map((m) => {
      if (m.id == left.id) return { ...m, end_time: boundary };
      if (m.id == right.id) return { ...m, start_time: boundary };
      return m;
    })
  );
}

// ---------------------------------------------------------------------------
// Настройки клиента. Живут в localStorage, к архиву и технике не привязаны.
// ---------------------------------------------------------------------------
const CLIENT_SETTINGS_KEY = "imu-client-settings";

const DEFAULT_CLIENT_SETTINGS = {
  // Делитель разрешения canvas: размер = css * dpr / значение.
  // 1.0 — как есть, 0.5 — вдвое чётче, 4.0 — вчетверо мягче и легче для GPU.
  canvasResolutionDivider: 1.0,
  // Возвращать маркер времени туда, где он был до правки границы сегмента.
  restorePlayheadAfterResize: true,
  // Куда сходятся соседи при удалении сегмента между ними:
  // "left" | "middle" | "right" | "none" (не схлопывать, оставить пробел).
  deleteSnapMode: "middle",
};

const clampResolutionDivider = (value) => {
  const num = Number(value);
  if (!isFinite(num)) return DEFAULT_CLIENT_SETTINGS.canvasResolutionDivider;
  return Math.min(4.0, Math.max(0.5, num));
};

const loadClientSettings = () => {
  try {
    const raw = localStorage.getItem(CLIENT_SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_CLIENT_SETTINGS };
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_CLIENT_SETTINGS,
      ...parsed,
      canvasResolutionDivider: clampResolutionDivider(parsed?.canvasResolutionDivider),
    };
  } catch {
    return { ...DEFAULT_CLIENT_SETTINGS };
  }
};

// ---------------------------------------------------------------------------
// Горячие клавиши.
// Одна функция и для назначения, и для распознавания: разойтись они уже не могут.
// ---------------------------------------------------------------------------
const formatHotkey = (event) =>
  (event.shiftKey ? "Shift+" : "") +
  (event.ctrlKey ? "Ctrl+" : "") +
  (event.metaKey ? "Meta+" : "") +
  (event.altKey ? "Alt+" : "") +
  event.code.replace("Key", "").replace("Digit", "");

const hotkeysStorageKey = (vehicleTypeId) => "hotkeys-" + vehicleTypeId;

// Читает раскладку типа техники и приводит её к текущему списку классов.
const loadHotkeys = (vehicleTypeId, availableClasses) => {
  let stored = {};
  if (vehicleTypeId) {
    try {
      stored = JSON.parse(localStorage.getItem(hotkeysStorageKey(vehicleTypeId))) || {};
    } catch {
      stored = {};
    }
  }
  const result = {};
  (availableClasses || []).forEach((element) => {
    result[element.id] = typeof stored[element.id] === "string" ? stored[element.id] : "";
  });
  return result;
};

const saveHotkeys = (vehicleTypeId, hotkeys) => {
  if (!vehicleTypeId) return;
  localStorage.setItem(hotkeysStorageKey(vehicleTypeId), JSON.stringify(hotkeys));
};

// ---------------------------------------------------------------------------
// Черновики.
// Отпечаток намеренно не включает цвет класса: перекраска класса сохраняется
// на сервере отдельно и черновиком считаться не должна.
// ---------------------------------------------------------------------------
const draftStorageKey = (archiveId) => archiveId + "-markup";

// ---------------------------------------------------------------------------
// Сдвиг видео относительно IMU.
// Время каждого кадра видео сдвигается на это значение, и маркер, графики и
// разметка живут во времени IMU. Если на видео событие происходит раньше, чем
// на графиках, сдвиг увеличивают. Хранится у архива; новый архив берёт
// последний сдвиг своей техники — часы камеры и датчиков одного телефона
// расходятся от часа к часу одинаково.
// ---------------------------------------------------------------------------
const videoOffsetKey = (archiveId) => `imu-video-offset-${archiveId}`;
const deviceVideoOffsetKey = (deviceId) => `imu-video-offset-device-${deviceId}`;

const loadVideoOffset = (archiveId, deviceId) => {
  try {
    const stored =
      localStorage.getItem(videoOffsetKey(archiveId)) ??
      (deviceId ? localStorage.getItem(deviceVideoOffsetKey(deviceId)) : null);
    const ms = Number(stored);
    return stored != null && Number.isFinite(ms) ? ms : 0;
  } catch {
    return 0;
  }
};

const saveVideoOffset = (archiveId, deviceId, ms) => {
  try {
    localStorage.setItem(videoOffsetKey(archiveId), String(ms));
    if (deviceId) localStorage.setItem(deviceVideoOffsetKey(deviceId), String(ms));
  } catch {
    // приватный режим — сдвиг не переживёт перезагрузку
  }
};

// ---------------------------------------------------------------------------
// Номер архива хранится только в query: /imu-viewer?archive=123.
// Дополнительный маршрут /imu-viewer/:archiveId не требуется.
// ---------------------------------------------------------------------------
const archiveIdFromUrl = () => {
  const query = new URLSearchParams(window.location.search).get("archive");
  return query && /^\d+$/.test(query) ? query : null;
};

// Сохраняем маршрут, остальные параметры, hash и состояние роутера.
const writeArchiveIdToUrl = (archiveId) => {
  const url = new URL(window.location.href);
  if (archiveId) url.searchParams.set("archive", String(archiveId));
  else url.searchParams.delete("archive");
  window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
};

// Поддерживаем и обычные массивы, и стандартную пагинацию DRF.
// next может содержать серверный origin: используем его путь через тот же API
// frontend, не отправляя Bearer-токен на адрес из ответа.
const fetchApiList = async (path, config) => {
  const list = [];
  const visited = new Set();
  const collectionPath = new URL(path, window.location.origin).pathname;
  let next = path;
  let pageConfig = config;
  while (next) {
    const url = new URL(next, window.location.origin);
    const pagePath = url.pathname + url.search;
    if (url.pathname !== collectionPath || visited.has(pagePath)) {
      throw new Error("Некорректная пагинация списка");
    }
    visited.add(pagePath);
    const { data } = await axios.get(pagePath, pageConfig);
    const rows = Array.isArray(data) ? data : data?.results;
    if (!Array.isArray(rows)) throw new Error("Некорректный формат списка");
    list.push(...rows);
    next = Array.isArray(data) ? null : data.next;
    pageConfig = { ...config, params: undefined };
  }
  return list;
};

// Без lookup принадлежность архива можно узнать по спискам доступной техники.
// Не больше четырёх запросов одновременно; после находки новые не начинаем.
const findArchiveDevice = async (archiveId, devices, loadArchives, isCurrent) => {
  let index = 0;
  let found = null;
  let failed = false;
  const worker = async () => {
    while (!found && isCurrent() && index < devices.length) {
      const device = devices[index++];
      try {
        const archives = await loadArchives(device.id);
        if (!isCurrent()) return;
        if (archives.some((a) => String(a.id) === archiveId)) found = { device, archives };
      } catch {
        failed = true;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(4, devices.length) }, worker));
  if (!isCurrent()) return null;
  if (!found && failed) throw new Error("Не все списки архивов удалось загрузить");
  return found;
};

const markupsFingerprint = (list) =>
  JSON.stringify(
    (list || [])
      .map((m) => [Number(m.id), m.class?.id, toMs(m.start_time), toMs(m.end_time)])
      .sort((a, b) => a[2] - b[2] || a[0] - b[0])
  );

// ---------------------------------------------------------------------------
// История правок разметки: Ctrl/⌘+Z — отмена, Ctrl/⌘+Shift+Z — повтор.
//
// Разметка и её история живут в одном редьюсере. Запись в историю — часть
// чистого перехода состояния, а не побочный эффект внутри setState, поэтому
// StrictMode с его двойным вызовом её не задваивает.
//
// Три вида изменений:
//   edit       — правка пользователя: прежнее состояние уходит в историю;
//   silent     — служебное обновление без следа в истории: промежуточные
//                кадры перетаскивания, подстановка имени и цвета класса;
//   checkpoint — точка отката перед серией silent (перетаскивание целиком —
//                один шаг отмены, а не шестьдесят).
// Загрузка с сервера — reset: новая отправная точка, история с нуля.
// ---------------------------------------------------------------------------
const MARKUP_HISTORY_LIMIT = 200;

const INITIAL_MARKUP_HISTORY = { past: [], present: [], future: [] };

const applyMarkupsUpdater = (updater, present) =>
  typeof updater === "function" ? updater(present) : updater;

const pushToHistory = (past, present) => {
  const next = past.length >= MARKUP_HISTORY_LIMIT ? past.slice(1) : past.slice();
  next.push(present);
  return next;
};

function markupHistoryReducer(state, action) {
  switch (action.type) {
    case "edit": {
      const next = applyMarkupsUpdater(action.updater, state.present);
      if (next === state.present) return state;
      // Правка, не поменявшая ничего по существу (те же сегменты, классы и
      // границы), шага отмены не создаёт — иначе Ctrl+Z «ничего не делал бы».
      if (markupsFingerprint(next) === markupsFingerprint(state.present)) {
        return { ...state, present: next };
      }
      return { past: pushToHistory(state.past, state.present), present: next, future: [] };
    }
    case "silent": {
      const next = applyMarkupsUpdater(action.updater, state.present);
      return next === state.present ? state : { ...state, present: next };
    }
    case "checkpoint":
      return { past: pushToHistory(state.past, state.present), present: state.present, future: [] };
    // Перетаскивание вернуло всё на место — пустой шаг отмены не нужен.
    case "dropCheckpointIfUnchanged": {
      if (!state.past.length) return state;
      const last = state.past[state.past.length - 1];
      if (markupsFingerprint(last) !== markupsFingerprint(state.present)) return state;
      return { ...state, past: state.past.slice(0, -1) };
    }
    case "reset":
      return { past: [], present: action.value || [], future: [] };
    case "undo": {
      if (!state.past.length) return state;
      return {
        past: state.past.slice(0, -1),
        present: state.past[state.past.length - 1],
        future: [state.present, ...state.future],
      };
    }
    case "redo": {
      if (!state.future.length) return state;
      return {
        past: pushToHistory(state.past, state.present),
        present: state.future[0],
        future: state.future.slice(1),
      };
    }
    default:
      return state;
  }
}

// По event.code, а не event.key: на русской раскладке та же клавиша даёт «я»,
// и проверка по key молча не срабатывала бы.
const isUndoRedoShortcut = (event) =>
  event.code === "KeyZ" && (event.ctrlKey || event.metaKey) && !event.altKey;

// ---------------------------------------------------------------------------
// Телеметрия разметки: сколько на деле занимает разметка одного архива.
//
// Это статистика, а не контроль. Время без ввода дольше порога (AFK) в
// активное не засчитывается. Но кроме готовых сумм отправляются и сырые
// 5-секундные корзины активности — порог AFK можно пересчитать на бэкенде
// задним числом, не собирая данные заново.
//
// Сессия = один заход на один архив. Сессии копятся в localStorage и уходят
// на сервер пачками. Не ушло (сеть, сервер ещё без эндпоинта) — остаётся в
// очереди до следующей попытки, ничего не теряется.
// ---------------------------------------------------------------------------
const TELEMETRY_SCHEMA_VERSION = 1;
// ImuLabelingSessionsView, см. backend_snippets/imu_labeling_session_view.py.
// null — выключить отправку (сессии будут только копиться).
const TELEMETRY_ENDPOINT = "/api/imu-archives/labeling-sessions/";
// Предел одной пачки в символах JSON. У Django по умолчанию тело запроса не
// больше 2,5 МБ (DATA_UPLOAD_MAX_MEMORY_SIZE) — держимся с запасом.
const TELEMETRY_BATCH_MAX_CHARS = 1_000_000;
const TELEMETRY_OUTBOX_KEY = "imu-telemetry-outbox";
const TELEMETRY_SNAPSHOT_PREFIX = "imu-telemetry-snapshot:";
const TELEMETRY_TAB_ID_KEY = "imu-telemetry-tab-id";
const TELEMETRY_OUTBOX_MAX_CHARS = 2_000_000; // ~2 МБ из ~5 МБ квоты localStorage
const TELEMETRY_EVENTS_LIMIT = 2000;
const TELEMETRY_TICK_MS = 1000;
const TELEMETRY_BUCKET_MS = 5000;
const TELEMETRY_SNAPSHOT_EVERY_MS = 15_000;
// Снимок чужой вкладки старше этого — вкладка умерла, сессию можно подобрать.
const TELEMETRY_STALE_SNAPSHOT_MS = 3 * 60_000;
// Нет ввода дольше — AFK. Пока видео играет, порог длиннее: разметчик
// может смотреть, ничего не нажимая.
const AFK_TIMEOUT_MS = 60_000;
const AFK_TIMEOUT_PLAYING_MS = 5 * 60_000;
// Прогресс видео свежее этого — считаем, что оно играет.
const PLAYBACK_FRESH_MS = 1500;
// Тик дольше этого — сон машины или усыплённая вкладка; сверх лимита время
// идёт в gap_ms, а не в активное или простой.
const TELEMETRY_MAX_TICK_CREDIT_MS = 5000;
// pointermove сыплется сотнями в секунду — для счётчиков берём не чаще этого.
const TELEMETRY_POINTERMOVE_SAMPLE_MS = 250;

// Флаги корзины активности.
const BUCKET_VISIBLE = 1;
const BUCKET_FOCUSED = 2;
const BUCKET_PLAYING = 4;
const BUCKET_ACTIVE = 8;

const makeTelemetryId = () =>
  window.crypto?.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const getTelemetryTabId = () => {
  try {
    let id = sessionStorage.getItem(TELEMETRY_TAB_ID_KEY);
    if (!id) {
      id = makeTelemetryId();
      sessionStorage.setItem(TELEMETRY_TAB_ID_KEY, id);
    }
    return id;
  } catch {
    return makeTelemetryId();
  }
};

// Итог разметки на момент снимка: сколько сегментов, сколько времени
// записи покрыто и как оно распределено по классам.
const computeMarkupOutcome = (markups, durationMs) => {
  const list = markups || [];
  let labeledMs = 0;
  const byClass = {};
  for (const m of list) {
    const ms = Math.max(0, toMs(m.end_time) - toMs(m.start_time));
    labeledMs += ms;
    const key = m.class?.id ?? "none";
    if (!byClass[key]) byClass[key] = { segments: 0, ms: 0 };
    byClass[key].segments += 1;
    byClass[key].ms += ms;
  }
  return {
    segments: list.length,
    labeled_ms: labeledMs,
    coverage: durationMs > 0 ? labeledMs / durationMs : null,
    classes_used: Object.keys(byClass).length,
    by_class: byClass,
  };
};

const readTelemetryOutbox = () => {
  try {
    return JSON.parse(localStorage.getItem(TELEMETRY_OUTBOX_KEY)) || [];
  } catch {
    return [];
  }
};

const writeTelemetryOutbox = (list) => {
  try {
    // Квота localStorage конечна: при переполнении вытесняем самые старые.
    let trimmed = list;
    let json = JSON.stringify(trimmed);
    while (json.length > TELEMETRY_OUTBOX_MAX_CHARS && trimmed.length > 1) {
      trimmed = trimmed.slice(Math.ceil(trimmed.length / 10));
      json = JSON.stringify(trimmed);
    }
    localStorage.setItem(TELEMETRY_OUTBOX_KEY, json);
  } catch {
    // приватный режим или квота — телеметрия не должна ломать страницу
  }
};

/**
 * Движок телеметрии без React: состояние в замыкании, наружу — методы.
 * Хук useLabelingTelemetry ниже создаёт его один раз на страницу.
 */
function createLabelingTelemetry() {
  const tabId = getTelemetryTabId();
  const snapshotKey = TELEMETRY_SNAPSHOT_PREFIX + tabId;

  let session = null;
  let lastMeta = null; // чтобы продолжить после возврата из bfcache
  let timer = null;
  let lastSnapshotAt = 0;
  let lastPointerMoveAt = 0;
  let getMarkups = () => [];
  let flushing = false;

  const perfNow = () => performance.now();
  const sinceStart = (now = perfNow()) => Math.round(now - session._startPerf);

  const bucketAt = (now) => {
    const index = Math.floor((now - session._startPerf) / TELEMETRY_BUCKET_MS);
    let bucket = session._buckets.get(index);
    if (!bucket) {
      bucket = [0, 0]; // [число вводов, флаги]
      session._buckets.set(index, bucket);
    }
    return bucket;
  };

  const pushEvent = (type, extra) => {
    if (session.events.length >= TELEMETRY_EVENTS_LIMIT) {
      session.events_truncated = true;
      return;
    }
    session.events.push(extra ? { t: sinceStart(), type, ...extra } : { t: sinceStart(), type });
  };

  const serialize = (endReason) => {
    const now = perfNow();
    const durationMs = session.archive.duration_ms;
    const { _startPerf, _lastTick, _lastInputAt, _lastPlaybackAt, _wasActive, _buckets, ...plain } = session;
    return {
      ...plain,
      ended_at: new Date().toISOString(),
      end_reason: endReason,
      snapshot_at: Date.now(),
      totals: { ...session.totals, wall_ms: Math.round(now - _startPerf) },
      buckets: [..._buckets.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([index, [inputs, flags]]) => [index, inputs, flags]),
      outcome: { ...computeMarkupOutcome(getMarkups(), durationMs), ...session.outcome },
    };
  };

  const writeSnapshot = () => {
    if (!session) return;
    lastSnapshotAt = perfNow();
    try {
      localStorage.setItem(snapshotKey, JSON.stringify(serialize("in_progress")));
    } catch {
      // квота — не критично, это только страховка от падения вкладки
    }
  };

  const clearSnapshot = () => {
    try {
      localStorage.removeItem(snapshotKey);
    } catch {
      // ignore
    }
  };

  // Сессии, оборвавшиеся без штатного завершения: падение вкладки, убитый
  // процесс. Свой снимок (та же вкладка, перезагрузка) берём сразу, чужой —
  // только если давно не обновлялся: иначе живая вкладка ещё пишет его.
  const recoverSnapshots = () => {
    let keys = [];
    try {
      keys = Object.keys(localStorage).filter((k) => k.startsWith(TELEMETRY_SNAPSHOT_PREFIX));
    } catch {
      return;
    }
    const recovered = [];
    for (const key of keys) {
      try {
        const snapshot = JSON.parse(localStorage.getItem(key));
        const own = key === snapshotKey;
        const stale = Date.now() - (snapshot?.snapshot_at || 0) > TELEMETRY_STALE_SNAPSHOT_MS;
        if (!snapshot || (!own && !stale)) continue;
        recovered.push({ ...snapshot, end_reason: "recovered" });
        localStorage.removeItem(key);
      } catch {
        // битый снимок не должен мешать остальным
      }
    }
    if (recovered.length) writeTelemetryOutbox([...readTelemetryOutbox(), ...recovered]);
  };

  // Отправка накопленного пачками по размеру.
  // sendBeacon не годится: он не умеет заголовок Authorization, поэтому
  // при уходе со страницы сессия только кладётся в очередь, а уходит при
  // следующем открытии.
  //
  // Сервер отвечает 2xx, даже если часть сессий пропустил (битые, устаревшие
  // снимки) — такие из очереди тоже уходят: повторять их бессмысленно, а
  // застряв, они тянули бы за собой остальные.
  const sessionKey = (s) => `${s.session_id}:${s.snapshot_at}`;

  const takeBatch = (outbox) => {
    const batch = [];
    let chars = 0;
    for (const session of outbox) {
      const size = JSON.stringify(session).length;
      // Хотя бы одну сессию берём всегда, даже если она одна больше предела.
      if (batch.length && chars + size > TELEMETRY_BATCH_MAX_CHARS) break;
      batch.push(session);
      chars += size;
    }
    return batch;
  };

  const flush = async () => {
    if (!TELEMETRY_ENDPOINT || flushing) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    flushing = true;
    // Уже отправленное в этом проходе. Если запись очереди в localStorage
    // молча не удалась, та же пачка вернулась бы снова — и цикл не кончился бы.
    const sentThisRun = new Set();
    try {
      for (;;) {
        const batch = takeBatch(readTelemetryOutbox());
        if (!batch.length || batch.some((s) => sentThisRun.has(sessionKey(s)))) break;
        await axios.post(
          TELEMETRY_ENDPOINT,
          { sessions: batch },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        // Пока шёл запрос, в очередь могли добавиться новые — их не трогаем.
        const sent = new Set(batch.map(sessionKey));
        sent.forEach((key) => sentThisRun.add(key));
        writeTelemetryOutbox(readTelemetryOutbox().filter((s) => !sent.has(sessionKey(s))));
      }
    } catch {
      // сеть или сервер — попробуем в следующий раз, очередь цела
    } finally {
      flushing = false;
    }
  };

  const endSession = (reason) => {
    if (!session) return;
    const finished = serialize(reason);
    session = null;
    writeTelemetryOutbox([...readTelemetryOutbox(), finished]);
    clearSnapshot();
    flush();
  };

  const startSession = (meta, { resumedFrom } = {}) => {
    endSession("archive_change");
    lastMeta = meta;
    const now = perfNow();
    session = {
      schema_version: TELEMETRY_SCHEMA_VERSION,
      session_id: makeTelemetryId(),
      tab_id: tabId,
      resumed_from: resumedFrom || null,
      started_at: new Date().toISOString(),
      client_tz_offset_min: new Date().getTimezoneOffset(),
      thresholds: {
        afk_timeout_ms: AFK_TIMEOUT_MS,
        afk_timeout_playing_ms: AFK_TIMEOUT_PLAYING_MS,
        playback_fresh_ms: PLAYBACK_FRESH_MS,
        bucket_ms: TELEMETRY_BUCKET_MS,
        tick_ms: TELEMETRY_TICK_MS,
      },
      archive: { ...meta.archive },
      context: { ...meta.context },
      client: {
        user_agent: navigator.userAgent,
        platform: navigator.userAgentData?.platform || navigator.platform || null,
        language: navigator.language,
        screen: `${window.screen?.width}x${window.screen?.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        dpr: window.devicePixelRatio || 1,
        settings: meta.settings || null,
      },
      totals: {
        active_ms: 0,
        idle_ms: 0,
        hidden_ms: 0,
        playback_ms: 0,
        active_playback_ms: 0,
        gap_ms: 0,
        afk_periods: 0,
        first_input_ms: null,
        last_input_ms: null,
      },
      input_counts: {},
      action_counts: {},
      events: [],
      events_truncated: false,
      outcome: {},
      _startPerf: now,
      _lastTick: now,
      _lastInputAt: now, // начало сессии — само по себе действие пользователя
      _lastPlaybackAt: -Infinity,
      _wasActive: true,
      _buckets: new Map(),
    };
    pushEvent("session_start");
    writeSnapshot();
  };

  const noteInput = (kind) => {
    if (!session) return;
    const now = perfNow();
    if (kind === "pointermove") {
      if (now - lastPointerMoveAt < TELEMETRY_POINTERMOVE_SAMPLE_MS) return;
      lastPointerMoveAt = now;
    }
    session.input_counts[kind] = (session.input_counts[kind] || 0) + 1;
    session._lastInputAt = now;
    const t = sinceStart(now);
    if (session.totals.first_input_ms == null) session.totals.first_input_ms = t;
    session.totals.last_input_ms = t;
    bucketAt(now)[0] += 1;
  };

  const tick = () => {
    if (!session) return;
    const now = perfNow();
    const delta = now - session._lastTick;
    session._lastTick = now;

    const visible = document.visibilityState === "visible";
    const focused = typeof document.hasFocus === "function" ? document.hasFocus() : visible;
    const sinceInput = now - session._lastInputAt;
    const playing = now - session._lastPlaybackAt < PLAYBACK_FRESH_MS;
    const active =
      visible &&
      (sinceInput < AFK_TIMEOUT_MS || (playing && sinceInput < AFK_TIMEOUT_PLAYING_MS));

    const credited = Math.min(delta, TELEMETRY_MAX_TICK_CREDIT_MS);
    const totals = session.totals;
    totals.gap_ms += Math.round(delta - credited);
    if (!visible) totals.hidden_ms += Math.round(credited);
    else if (active) totals.active_ms += Math.round(credited);
    else totals.idle_ms += Math.round(credited);
    if (playing) {
      totals.playback_ms += Math.round(credited);
      if (active) totals.active_playback_ms += Math.round(credited);
    }

    if (session._wasActive && !active) {
      totals.afk_periods += 1;
      pushEvent(visible ? "afk_start" : "hidden_start");
    } else if (!session._wasActive && active) {
      pushEvent("afk_end");
    }
    session._wasActive = active;

    bucketAt(now)[1] |=
      (visible ? BUCKET_VISIBLE : 0) |
      (focused ? BUCKET_FOCUSED : 0) |
      (playing ? BUCKET_PLAYING : 0) |
      (active ? BUCKET_ACTIVE : 0);

    if (now - lastSnapshotAt >= TELEMETRY_SNAPSHOT_EVERY_MS) writeSnapshot();
  };

  // Захват на window: ручки сегментов зовут stopPropagation, а до фазы
  // захвата их события доходят всё равно.
  const inputListeners = ["pointerdown", "keydown", "wheel", "pointermove"].map((kind) => [
    kind,
    () => noteInput(kind),
  ]);

  const onVisibilityChange = () => {
    if (!session) return;
    pushEvent(document.visibilityState === "visible" ? "tab_visible" : "tab_hidden");
    // Скрытая вкладка может так и не проснуться — сохраняемся заранее.
    if (document.visibilityState !== "visible") writeSnapshot();
  };

  // pagehide надёжнее beforeunload и срабатывает и при закрытии, и при
  // перезагрузке, и при уходе в bfcache.
  const onPageHide = () => endSession("pagehide");

  const onPageShow = (event) => {
    if (event.persisted && !session && lastMeta) {
      startSession(lastMeta, { resumedFrom: "bfcache" });
    }
  };

  return {
    mount() {
      recoverSnapshots();
      inputListeners.forEach(([kind, fn]) =>
        window.addEventListener(kind, fn, { capture: true, passive: true })
      );
      document.addEventListener("visibilitychange", onVisibilityChange);
      window.addEventListener("pagehide", onPageHide);
      window.addEventListener("pageshow", onPageShow);
      timer = setInterval(tick, TELEMETRY_TICK_MS);
      flush();
    },
    unmount(reason) {
      endSession(reason);
      inputListeners.forEach(([kind, fn]) =>
        window.removeEventListener(kind, fn, { capture: true })
      );
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      clearInterval(timer);
      timer = null;
    },
    startSession,
    endSession,
    setMarkupsSource(fn) {
      getMarkups = fn;
    },
    // Дополнить сведения об архиве по мере загрузки (длительность — после CSV).
    updateArchive(patch, eventType) {
      if (!session) return;
      Object.assign(session.archive, patch);
      if (eventType) pushEvent(eventType, patch);
    },
    // Действие пользователя: счётчик + запись в журнал событий.
    track(type, extra) {
      if (!session) return;
      session.action_counts[type] = (session.action_counts[type] || 0) + 1;
      pushEvent(type, extra);
    },
    // Частое событие: только счётчик, без журнала (перемотки на каждом кадре).
    count(type) {
      if (!session) return;
      session.action_counts[type] = (session.action_counts[type] || 0) + 1;
    },
    // Видео сдвинулось само — значит, играет.
    notePlayback() {
      if (session) session._lastPlaybackAt = perfNow();
    },
    noteOutcome(patch) {
      if (session) Object.assign(session.outcome, patch);
    },
  };
}

const useLabelingTelemetry = () => {
  const ref = useRef(null);
  if (!ref.current) ref.current = createLabelingTelemetry();
  const telemetry = ref.current;

  useEffect(() => {
    telemetry.mount();
    return () => telemetry.unmount("page_leave");
  }, [telemetry]);

  return telemetry;
};

const NnMarkupGraph = ({
  data,
  viewStart,
  setViewStart,
  viewDuration,
  setViewDuration,
  canvasResolutionDivider,
}) => {
  const [charts, setCharts] = useState([]);

  // Всё, что нужно отрисовке, лежит в ref: поэтому drawGraphs и scheduleDraw
  // создаются один раз за жизнь компонента, и эффект слежения за видом
  // зависит ровно от viewStart и viewDuration.
  const modelRef = useRef(null);
  const chartsRef = useRef([]);
  const canvasRefs = useRef([]);
  const ctxRefs = useRef([]);
  const sizesRef = useRef([]);
  const viewRef = useRef({ start: viewStart, duration: viewDuration });
  const rafRef = useRef(0);
  const textWidthRef = useRef(0);

  // Теперь это обычное число из настроек клиента, а не ref.
  const dividerRef = useRef(canvasResolutionDivider);
  dividerRef.current = clampResolutionDivider(canvasResolutionDivider);

  const setViewStartRef = useRef(setViewStart);
  const setViewDurationRef = useRef(setViewDuration);
  setViewStartRef.current = setViewStart;
  setViewDurationRef.current = setViewDuration;

  // --- 1. Построение пирамиды: один раз на сенсор, а не на каждую ось ---
  useEffect(() => {
    if (!data) return;

    const sensors = [];
    const nextCharts = [];

    for (const def of SENSOR_DEFS) {
      const rows = data[def.key];
      if (!rows || rows.length === 0) continue;

      const sensorIndex = sensors.length;
      sensors.push({ levels: buildSensorPyramid(rows, def.axes) });

      def.axes.forEach((axis, axisIndex) => {
        nextCharts.push({
          name: axis.name,
          label: axis.label,
          color: axis.color,
          sensorIndex,
          axisIndex,
        });
      });
    }

    if (nextCharts.length === 0) return;

    modelRef.current = { sensors, sensorLod: [] };
    chartsRef.current = nextCharts;
    canvasRefs.current.length = nextCharts.length;
    ctxRefs.current.length = nextCharts.length;
    sizesRef.current.length = nextCharts.length;
    setCharts(nextCharts);

    setViewStartRef.current(data.minTimeMs);
    setViewDurationRef.current(data.maxTimeMs - data.minTimeMs);
  }, [data]);

  const getDividerIndex = useCallback((W, duration) => {
    if (!duration || !isFinite(duration)) return 0;

    if (!textWidthRef.current) {
      const measureCanvas = document.createElement("canvas");
      const measureCtx = measureCanvas.getContext("2d");
      measureCtx.font = "1rem monospace";
      textWidthRef.current = measureCtx.measureText("ツ").width;
    }
    const textWidth = textWidthRef.current;

    let resultDivider = 0;
    for (; resultDivider < DIVIDERS.length; resultDivider++) {
      if (W - (duration / DIVIDERS[resultDivider]) * textWidth * WIDTH_LETTERS[resultDivider] > 0) break;
    }
    return Math.min(resultDivider, DIVIDERS.length - 1);
  }, []);

  // Подстраховка на случай, если ResizeObserver ещё не отдал размер:
  // меряем сами один раз, а не в каждом кадре.
  const ensureSize = useCallback((canvas, index) => {
    let size = sizesRef.current[index];
    if (size && size.w > 0 && size.h > 0) return size;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const divider = dividerRef.current || 1;
    const w = Math.round((rect.width * dpr) / divider);
    const h = Math.round((rect.height * dpr) / divider);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      ctxRefs.current[index] = null;
    }
    size = { w, h };
    sizesRef.current[index] = size;
    return size;
  }, []);

  // --- 2. Отрисовка ---
  const drawGraphs = useCallback(() => {
    const model = modelRef.current;
    const charts = chartsRef.current;
    if (!model || charts.length === 0) return;

    const { start: minTime, duration } = viewRef.current;
    const maxTime = minTime + duration;
    if (!isFinite(minTime) || !(duration > 0)) return;

    const invSpan = 1 / duration;
    const sensorLod = model.sensorLod;
    sensorLod.length = 0;

    for (let i = 0; i < charts.length; i++) {
      const chart = charts[i];
      const canvas = canvasRefs.current[i];
      if (!canvas) continue;

      const size = ensureSize(canvas, i);
      if (!size || size.w === 0 || size.h === 0) continue;
      const W = size.w;
      const H = size.h;

      let ctx = ctxRefs.current[i];
      if (!ctx) {
        ctx = canvas.getContext("2d", { alpha: false });
        ctxRefs.current[i] = ctx;
      }
      if (!ctx) continue;

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, W, H);

      // --- сетка времени ---
      const step = DIVIDERS[getDividerIndex(W, duration)];
      if (step > 0) {
        ctx.strokeStyle = "#00000020";
        ctx.lineWidth = 1;
        const offset = Math.round(step * (Math.ceil(minTime / step) - minTime / step));
        ctx.beginPath();
        for (let t = offset; minTime + t < maxTime; t += step) {
          const px = Math.round(t * invSpan * W) + 0.5;
          ctx.moveTo(px, 0);
          ctx.lineTo(px, H);
        }
        ctx.stroke();
      }

      // --- выбор уровня сжатия: считается один раз на сенсор ---
      const sensor = model.sensors[chart.sensorIndex];
      let lod = sensorLod[chart.sensorIndex];
      if (lod === undefined) {
        const base = sensor.levels[0].t;
        const pointsInView = Math.max(1, lowerBound(base, maxTime) - lowerBound(base, minTime));
        lod = Math.max(0, Math.min(Math.floor(Math.log2(pointsInView / W)), sensor.levels.length - 1));
        sensorLod[chart.sensorIndex] = lod;
      }

      const level = sensor.levels[lod];
      const ts = level.t;
      const vs = level.v[chart.axisIndex];

      const start = Math.max(0, lowerBound(ts, minTime) - 1);
      const end = Math.min(ts.length, lowerBound(ts, maxTime) + 2);
      if (end - start <= 0) continue;

      // --- локальный min/max строго по этой оси ---
      let yMin = Infinity;
      let yMax = -Infinity;
      for (let k = start; k < end; k++) {
        const val = vs[k];
        if (val < yMin) yMin = val;
        if (val > yMax) yMax = val;
      }

      let range = yMax - yMin;
      if (range === 0 || !isFinite(range)) {
        yMax += 1;
        yMin -= 1;
        range = 2;
      }

      const plotH = H - 8;
      const baseY = H - 4;
      const invRange = 1 / range;

      // --- шкала значений Y ---
      ctx.strokeStyle = "#00000010";
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let s = 0; s <= 2; s++) {
        const gy = Math.round(baseY - (s / 2) * plotH) + 0.5;
        ctx.moveTo(0, gy);
        ctx.lineTo(W, gy);
      }
      ctx.stroke();

      ctx.fillStyle = "#00000080";
      ctx.font = "11px monospace";
      ctx.textAlign = "left";
      for (let s = 0; s <= 2; s++) {
        const val = yMin + range * (s / 2);
        const py = baseY - (s / 2) * plotH;
        ctx.fillText(val.toFixed(2), 5, py < 12 ? py + 12 : py - 3);
      }

      // --- линия ---
      ctx.strokeStyle = chart.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo((ts[start] - minTime) * invSpan * W, baseY - (vs[start] - yMin) * invRange * plotH);
      for (let k = start + 1; k < end; k++) {
        ctx.lineTo((ts[k] - minTime) * invSpan * W, baseY - (vs[k] - yMin) * invRange * plotH);
      }
      ctx.stroke();
    }
  }, [ensureSize, getDividerIndex]);

  // Один кадр на анимационный фрейм, сколько бы событий ни пришло:
  // запланированный кадр прочитает уже свежий viewRef.
  const scheduleDraw = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      drawGraphs();
    });
  }, [drawGraphs]);

  // --- 3. Панорама и зум ---
  useEffect(() => {
    const v = viewRef.current;
    if (v.start === viewStart && v.duration === viewDuration) return;
    viewRef.current = { start: viewStart, duration: viewDuration };
    scheduleDraw();
  }, [viewStart, viewDuration, scheduleDraw]);

  // --- 4. Размеры канвасов: только из ResizeObserver, без замеров в кадре ---
  useEffect(() => {
    if (charts.length === 0) return;

    const applySize = (canvas, index, cssWidth, cssHeight) => {
      const dpr = window.devicePixelRatio || 1;
      const divider = dividerRef.current || 1;
      const w = Math.round((cssWidth * dpr) / divider);
      const h = Math.round((cssHeight * dpr) / divider);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        ctxRefs.current[index] = null;
      }
      sizesRef.current[index] = { w, h };
    };

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const index = canvasRefs.current.indexOf(entry.target);
        if (index === -1) continue;
        applySize(entry.target, index, entry.contentRect.width, entry.contentRect.height);
      }
      drawGraphs();
    });

    for (let i = 0; i < charts.length; i++) {
      const canvas = canvasRefs.current[i];
      if (!canvas) continue;
      const rect = canvas.getBoundingClientRect();
      applySize(canvas, i, rect.width, rect.height);
      observer.observe(canvas);
    }

    // Первый кадр — синхронно, иначе он может уйти с нулевыми размерами.
    drawGraphs();

    return () => observer.disconnect();
  }, [charts, drawGraphs]);

  // Смена разрешения в настройках не меняет css-размер, поэтому ResizeObserver
  // молчит — сбрасываем кэш размеров вручную, ensureSize перемерит в кадре.
  useEffect(() => {
    if (charts.length === 0) return;
    for (let i = 0; i < charts.length; i++) sizesRef.current[i] = null;
    drawGraphs();
  }, [canvasResolutionDivider, charts, drawGraphs]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  // --- 5. Разметка мемоизирована: при смене вида React не заходит в поддерево ---
  const body = useMemo(() => {
    if (charts.length === 0) return null;
    return (
      <div className="d-flex flex-column gap-1 w-100 flex-grow-1" style={{ height: "calc(100% - 7.5rem)" }}>
        {charts.map((chart, index) => (
          <div
            key={chart.name}
            className="bg-white border rounded shadow-sm w-100 d-flex flex-column"
            style={{ flex: "1 1 0", minHeight: "65px" }}
          >
            <div
              className="d-flex align-items-center justify-content-between px-2 py-0 bg-light border-bottom rounded-top flex-shrink-0"
              style={{ height: "20px" }}
            >
              <span className="fw-bold" style={{ fontSize: "0.72rem" }}>
                {chart.name}
              </span>
              <span style={{ color: chart.color, fontWeight: "bold", fontSize: "0.75rem" }}>
                ■ {chart.label.toUpperCase()}
              </span>
            </div>
            <div className="flex-grow-1 position-relative w-100 h-100 overflow-hidden">
              <canvas
                ref={(el) => (canvasRefs.current[index] = el)}
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "block" }}
                className="nn-markup-canvas"
              />
            </div>
          </div>
        ))}
      </div>
    );
  }, [charts]);

  return body;
};

const NnMarkupSegments = ({
  markups,
  markupsHistory,
  aiSessions,
  selectedClass,
  setSelectedClass,
  newSegmentElement,
  viewStart,
  viewDuration,
  containerRef,
  makeId,
  videoTimeMs,
  setVideoTimeMs,
  restorePlayheadAfterResize,
  deleteSnapMode,
}) => {
  const [selectedSegmentId, setSelectedSegmentId] = useState(null);

  // Зеркала пропсов присваиваем в рендере: обработчик указателя всегда видит
  // актуальные значения, и на это не тратится отдельный эффект с задержкой.
  const markupsRef = useRef(markups);
  markupsRef.current = markups;
  const viewStartRef = useRef(viewStart);
  viewStartRef.current = viewStart;
  const viewDurationRef = useRef(viewDuration);
  viewDurationRef.current = viewDuration;
  const selectedSegmentIdRef = useRef(selectedSegmentId);
  selectedSegmentIdRef.current = selectedSegmentId;
  const makeIdRef = useRef(makeId);
  makeIdRef.current = makeId;
  const videoTimeMsRef = useRef(videoTimeMs);
  videoTimeMsRef.current = videoTimeMs;
  const restorePlayheadRef = useRef(restorePlayheadAfterResize);
  restorePlayheadRef.current = restorePlayheadAfterResize;
  const deleteSnapModeRef = useRef(deleteSnapMode);
  deleteSnapModeRef.current = deleteSnapMode;

  const dragRef = useRef(null);
  // Последний «чистый» щелчок по ручке (без сдвига) — для своего распознавания
  // двойного клика. См. handlePointerUp.
  const lastHandleClickRef = useRef(null);

  // Сдвиг курсора, после которого нажатие считается перетаскиванием, а не
  // щелчком. Дрожание руки при щелчке не должно двигать границу.
  const DRAG_THRESHOLD_PX = 3;
  const DOUBLE_CLICK_MS = 400;

  // Время под курсором. Абсолютная позиция, а не накопление movementX:
  // не зависит от devicePixelRatio, объединения событий и пропущенных кадров.
  const cursorTime = (clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    return viewStartRef.current + ((clientX - rect.left) / rect.width) * viewDurationRef.current;
  };

  const handlePointerDown = (side) => (e) => {
    e.stopPropagation();
    // Только основная кнопка: правый клик не должен начинать перетаскивание.
    if (e.button !== 0) return;
    const segment = markupsRef.current.find((m) => m.id == selectedSegmentIdRef.current);
    if (!segment) return;

    const boundaryMs = toMs(side === "left" ? segment.start_time : segment.end_time);
    dragRef.current = {
      side,
      base: markupsRef.current,
      grabOffsetMs: boundaryMs - cursorTime(e.clientX),
      // Куда вернуть маркер времени после правки, если включена настройка.
      playheadBefore: videoTimeMsRef.current,
      startX: e.clientX,
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    // Кнопка уже отпущена, а pointerup до ручки не дошёл (захват сняло без него:
    // ручка перерисовалась или пропала посреди жеста, кнопку отпустили за окном,
    // правый клик открыл меню). Без этой проверки перетаскивание оставалось
    // «живым», и граница ехала за курсором при простом наведении на ручку.
    if ((e.buttons & 1) === 0) {
      endDrag();
      return;
    }

    // Пока курсор не ушёл дальше порога, это ещё щелчок, а не перетаскивание.
    if (!drag.moved) {
      if (Math.abs(e.clientX - drag.startX) < DRAG_THRESHOLD_PX) return;
      drag.moved = true;
      // Перетаскивание целиком — один шаг отмены. Точку отката ставим на
      // первом настоящем сдвиге, а не на нажатии: щелчок без сдвига не должен
      // оставлять в истории пустой шаг.
      markupsHistory.checkpoint("boundary_drag", { side: drag.side });
    }

    const segment = drag.base.find((m) => m.id == selectedSegmentIdRef.current);
    if (!segment) return;

    const rect = containerRef.current.getBoundingClientRect();
    const minMs = (viewDurationRef.current / rect.width) * 3; // не тоньше 3 px
    const raw = cursorTime(e.clientX) + drag.grabOffsetMs;

    // Ограничение считаем здесь же, чтобы маркер встал ровно на границу,
    // а не убежал за неё, когда сегмент упёрся в минимальную ширину.
    const boundaryMs =
      drag.side === "left"
        ? Math.min(raw, toMs(segment.end_time) - minMs)
        : Math.max(raw, toMs(segment.start_time) + minMs);

    // Промежуточные кадры — без следа в истории, точка отката уже стоит.
    markupsHistory.silent(
      resizeSegment(drag.base, selectedSegmentIdRef.current, drag.side, boundaryMs, minMs, makeIdRef.current)
    );
    setVideoTimeMs(boundaryMs);
  };

  const releaseCapture = (e) => {
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const handlePointerUp = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    releaseCapture(e);

    if (drag.moved) {
      // Перетаскивание обрывает серию щелчков. Раньше двойной клик ловился
      // нативным dblclick, а браузер засчитывает его и тогда, когда одно из
      // двух нажатий было перетаскиванием: ручка едет за курсором, отпускание
      // приходится на неё же, и получается «щелчок». Итог — после серии
      // мелких подтягиваний граница сама прыгала к соседу или к маркеру.
      lastHandleClickRef.current = null;
      markupsHistory.dropCheckpointIfUnchanged();
      if (restorePlayheadRef.current && drag.playheadBefore != null) {
        setVideoTimeMs(drag.playheadBefore);
      }
      return;
    }

    const id = selectedSegmentIdRef.current;
    const segment = markupsRef.current.find((m) => m.id == id);
    if (!segment) return;

    // Щелчок ставит маркер на точное время края, а не на время под мышью:
    // ушко имеет ширину, и клик внутри него не обязан попадать в саму границу.
    const boundaryMs = toMs(drag.side === "left" ? segment.start_time : segment.end_time);
    const now = performance.now();
    const last = lastHandleClickRef.current;
    if (last && last.id == id && last.side === drag.side && now - last.at < DOUBLE_CLICK_MS) {
      lastHandleClickRef.current = null;
      // Первый клик уже перемотал видео. Для прежнего смысла двойного клика
      // snapEdge должен видеть маркер, который был ДО первого нажатия.
      snapEdge(drag.side, last.playheadBefore);
    } else {
      lastHandleClickRef.current = { id, side: drag.side, at: now, playheadBefore: drag.playheadBefore };
      setVideoTimeMs(boundaryMs);
    }
  };

  // Жест оборвался без pointerup (отмена, потеря захвата, кнопку отпустили там,
  // где ручка этого не услышала) — не щелчок и не повод продолжать серию.
  // Уже применённое перетаскивание остаётся как есть.
  const endDrag = () => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    lastHandleClickRef.current = null;

    if (drag.moved) markupsHistory.dropCheckpointIfUnchanged();
    if (drag.moved && restorePlayheadRef.current && drag.playheadBefore != null) {
      setVideoTimeMs(drag.playheadBefore);
    }
  };

  const handlePointerCancel = (e) => {
    releaseCapture(e);
    endDrag();
  };

  // Захват снят: после нашего pointerup dragRef уже пуст и это ничего не делает;
  // иначе жест оборвался — завершаем, а не ждём pointerup, которого не будет.
  const handleLostPointerCapture = () => endDrag();

  // Страховка на случай, когда ручка пропала из DOM посреди жеста: тогда и
  // lostpointercapture приходит не ей, а документу. Любое отпускание кнопки
  // или уход из окна завершает висящий жест. Обычный pointerup ручки
  // срабатывает раньше и успевает очистить dragRef.
  const endDragRef = useRef(endDrag);
  endDragRef.current = endDrag;
  useEffect(() => {
    const onRelease = () => {
      if (dragRef.current) endDragRef.current();
    };
    window.addEventListener("pointerup", onRelease);
    window.addEventListener("pointercancel", onRelease);
    window.addEventListener("blur", onRelease);
    return () => {
      window.removeEventListener("pointerup", onRelease);
      window.removeEventListener("pointercancel", onRelease);
      window.removeEventListener("blur", onRelease);
    };
  }, []);

  /**
   * Двойной клик по ручке отправляет край наружу, к ближайшей преграде с этой
   * стороны: к обращённому краю ближайшего соседа либо к маркеру времени, если
   * тот стоит перед соседом. Соседа нет — идём к маркеру, маркера нет —
   * не делаем ничего.
   *
   * Считаем только то, что лежит СНАРУЖИ от края: маркер внутри сегмента
   * границу не двигает, иначе двойной клик молча срезал бы размеченное.
   */
  const snapEdge = (side, marker = videoTimeMsRef.current) => {
    const markups = markupsRef.current;
    const id = selectedSegmentIdRef.current;
    const segment = markups.find((m) => m.id == id);
    if (!segment) return;

    const edge = toMs(side === "left" ? segment.start_time : segment.end_time);
    const candidates = [];

    if (side === "left") {
      let nearestNeighbor = -Infinity;
      for (const m of markups) {
        if (m.id == segment.id) continue;
        const end = toMs(m.end_time);
        if (end <= edge && end > nearestNeighbor) nearestNeighbor = end;
      }
      if (isFinite(nearestNeighbor)) candidates.push(nearestNeighbor);
      if (marker != null && marker < edge) candidates.push(marker);
    } else {
      let nearestNeighbor = Infinity;
      for (const m of markups) {
        if (m.id == segment.id) continue;
        const start = toMs(m.start_time);
        if (start >= edge && start < nearestNeighbor) nearestNeighbor = start;
      }
      if (isFinite(nearestNeighbor)) candidates.push(nearestNeighbor);
      if (marker != null && marker > edge) candidates.push(marker);
    }

    if (candidates.length === 0) return;

    // Ближайшая к краю преграда: маркер выигрывает ровно тогда,
    // когда стоит перед соседом.
    const target = side === "left" ? Math.max(...candidates) : Math.min(...candidates);
    if (target === edge) return; // сосед уже вплотную — двигать некуда

    const rect = containerRef.current.getBoundingClientRect();
    const minMs = (viewDurationRef.current / rect.width) * 3;

    markupsHistory.edit(
      resizeSegment(markups, id, side, target, minMs, makeIdRef.current),
      "edge_snap",
      { side, to: target === marker ? "marker" : "neighbor" }
    );
  };

  const handleSegmentSelect = (e) => {
    const newId = e.target.dataset.id;
    setSelectedSegmentId((prev) => (newId === prev ? null : newId));
  };

  // Последний замеченный класс выбранного сегмента — см. эффект синхронизации
  // панели после отмены ниже.
  const selectedMarkupClassIdRef = useRef();

  // Выбор сегмента подтягивает его класс в панель.
  useEffect(() => {
    const markup = selectedSegmentId
      ? markupsRef.current.find((m) => m.id == selectedSegmentId)
      : null;
    // Запоминаем класс уже нового выделения, иначе синхронизация ниже
    // сравнивала бы с классом предыдущего выбранного сегмента.
    selectedMarkupClassIdRef.current = markup?.class?.id;
    if (markup) setSelectedClass(markup.class);
  }, [selectedSegmentId]);

  // Смена класса в панели применяется к выбранному сегменту.
  useEffect(() => {
    const id = selectedSegmentIdRef.current;
    if (!id || !selectedClass) return;
    const markup = markupsRef.current.find((m) => m.id == id);
    if (!markup) return;
    if (JSON.stringify(markup.class) === JSON.stringify(selectedClass)) return;

    // Смена класса может сделать сегмент однородным с соседом — тогда сливаем,
    // сохраняя выбранный id, чтобы выделение и ручки остались на месте.
    markupsHistory.edit(
      (prev) =>
        mergeAdjacentSameClass(
          prev.map((m) => (m.id == id ? { ...m, class: selectedClass } : m)),
          id
        ),
      "class_change",
      { from_class_id: markup.class?.id ?? null, to_class_id: selectedClass.id ?? null }
    );
  }, [selectedClass]);

  // После отмены или повтора у выбранного сегмента мог смениться класс.
  // Панель должна показывать его, а не тот, что был выбран до отката.
  //
  // Подтягиваем панель, только если класс сегмента сменился «под ней»: панель
  // показывала прежний класс сегмента, а сегмент теперь другого. Когда класс
  // меняет сам пользователь через панель, панель уже показывает новый — и
  // эффект ничего не трогает. Сравнение по id: имя и цвет подставляются
  // отдельно и не повод дёргать панель.
  useEffect(() => {
    const id = selectedSegmentIdRef.current;
    const markup = id ? markups.find((m) => m.id == id) : null;
    const classId = markup?.class?.id;
    const prevClassId = selectedMarkupClassIdRef.current;
    selectedMarkupClassIdRef.current = classId;

    if (!markup || classId == prevClassId) return;
    if (selectedClass?.id == prevClassId) setSelectedClass(markup.class);
  }, [markups]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Delete") return;
      const id = selectedSegmentIdRef.current;
      if (id == null) return;
      markupsHistory.edit(
        (prev) => deleteSegment(prev, id, deleteSnapModeRef.current),
        "segment_delete",
        { snap_mode: deleteSnapModeRef.current }
      );
      setSelectedSegmentId(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [markupsHistory]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setSelectedSegmentId(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Разметка считается в рендере, а не складывается в состояние эффектами:
  // раньше на одно движение мыши шло три прохода рендера подряд.
  const segmentElements = useMemo(() => {
    if (!markups) return null;
    return markups.map((element) => {
      const start = toMs(element.start_time);
      const end = toMs(element.end_time);
      const isSelected = element.id == selectedSegmentId;

      return (
        <div
          key={`markup-segment-${element.id}`}
          id={"markup-segment-" + element.id}
          data-id={element.id}
          onClick={handleSegmentSelect}
          style={{
            backgroundColor: element.class.class_color,
            zIndex: isSelected ? "2" : "1",
            fontWeight: isSelected ? "bold" : "normal",
            position: "absolute",
            height: "100%",
            left: Math.max(((start - viewStart) / viewDuration) * 100, 0) + "%",
            display: (end - viewStart) / viewDuration < 0 ? "none" : "block",
            width:
              ((end - start) / viewDuration) * 100 +
              Math.min(((start - viewStart) / viewDuration) * 100, 0) +
              "%",
            alignContent: "center",
            textOverflow: "clip",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          &emsp;{element.class.class_name}
        </div>
      );
    });
  }, [markups, selectedSegmentId, viewStart, viewDuration]);

  const handlesElement = useMemo(() => {
    if (!markups) return null;
    const markup = markups.find((m) => m.id == selectedSegmentId);
    if (!markup) return null;

    const start = toMs(markup.start_time);
    const end = toMs(markup.end_time);

    // У соседней границы потянутся оба сегмента сразу — помечаем сплошной рамкой.
    const hasLeftNeighbor = markups.some((m) => m.id != markup.id && areAdjacent(m, markup));
    const hasRightNeighbor = markups.some((m) => m.id != markup.id && areAdjacent(markup, m));

    const startPct = ((start - viewStart) / viewDuration) * 100;
    const endPct = ((end - viewStart) / viewDuration) * 100;
    // Ручка не шире половины сегмента. Иначе на узком сегменте (уже двух ручек
    // на экране) правая, лежащая в DOM позже, целиком накрывала левую: хватаешь
    // левый край — тянется правый, а он упирается в минимальную ширину.
    const handleWidth = `min(1rem, ${(endPct - startPct) / 2}%)`;

    const handleStyle = {
      backgroundColor: markup.class.class_color,
      filter: "brightness(200%)",
      zIndex: "2",
      position: "absolute",
      height: "100%",
      top: "0",
      width: handleWidth,
      boxSizing: "border-box",
      pointerEvents: "auto",
      cursor: "ew-resize",
      touchAction: "none",
    };

    return (
      <div
        id="markup-resize-handles"
        style={{ position: "absolute", height: "100%", width: "100%", pointerEvents: "none" }}
      >
        <div
          id="markup-resize-handle-left"
          style={{
            ...handleStyle,
            border: hasLeftNeighbor ? "black solid 2px" : "black dashed 2px",
            left: startPct + "%",
          }}
          onPointerDown={handlePointerDown("left")}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onLostPointerCapture={handleLostPointerCapture}
          title="Клик — маркер на левую границу; двойной клик — граница к соседу слева или к прежнему маркеру"
        />
        <div
          id="markup-resize-handle-right"
          style={{
            ...handleStyle,
            border: hasRightNeighbor ? "black solid 2px" : "black dashed 2px",
            left: `calc(${endPct}% - ${handleWidth})`,
          }}
          onPointerDown={handlePointerDown("right")}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onLostPointerCapture={handleLostPointerCapture}
          title="Клик — маркер на правую границу; двойной клик — граница к соседу справа или к прежнему маркеру"
        />
      </div>
    );
  }, [markups, selectedSegmentId, viewStart, viewDuration]);

  return (
    <div style={{ height: "5rem", width: "100%" }}>
      <div style={{ height: "50%", width: "100%", position: "relative" }}>
        {segmentElements}
        {newSegmentElement}
        {handlesElement}
      </div>
    </div>
  );
};

const NnMarkupTimeline = ({
  markups,
  markupsHistory,
  aiSessions,
  data,
  newSegment,
  videoTimeMs,
  setVideoTimeMs,
  selectedClass,
  setSelectedClass,
  timezoneOffset,
  makeId,
  clientSettings,
}) => {
  const [viewStart, setViewStart] = useState(Infinity);
  const [viewDuration, setViewDuration] = useState(0);

  const isDraggingPlayhead = useRef(false);

  const containerRef = useRef(null);

  const [timeMarks, setTimeMarks] = useState([]);

  const viewStartRef = useRef(viewStart);
  useEffect(() => {
    viewStartRef.current = viewStart
  }, [viewStart])
  const viewDurationRef = useRef(viewDuration);
  useEffect(() => {
    viewDurationRef.current = viewDuration
  }, [viewDuration])

  // Читает только ref-ы и стабильные сеттеры, поэтому зависимостей нет —
  // и обработчик клавиатуры может держать её без риска устаревшего замыкания.
  const performZoom = useCallback((zoomFactor, anchorXPercent) => {
    const anchorTime = viewStartRef.current + viewDurationRef.current * anchorXPercent;
    const newStart = anchorTime - viewDurationRef.current * zoomFactor * anchorXPercent;
    setViewStart(newStart);
    setViewDuration(viewDurationRef.current * zoomFactor);
  }, []);

  const handleWheel = (e) => {
      e.preventDefault();
      const rect = containerRef.current.getBoundingClientRect();
      const multiplier = e.ctrlKey ? 1.1 : 1.25
      performZoom(
        e.deltaY > 0 ? multiplier : 1 / multiplier,
        (e.clientX - rect.left) / rect.width,
      );
  };

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.addEventListener("wheel", handleWheel, { passive: false });
  }, [containerRef]);

  // Стрелки: влево/вправо — панорама, вверх/вниз — зум.
  // Шаг панорамы в долях видимого окна, поэтому на любом зуме прокрутка
  // ощущается одинаково. Shift — крупный шаг.
  useEffect(() => {
    const PAN_KEYS = ["ArrowLeft", "ArrowRight"];
    const ZOOM_KEYS = ["ArrowUp", "ArrowDown"];

    const onKeyDown = (event) => {
      const isPan = PAN_KEYS.includes(event.key);
      const isZoom = ZOOM_KEYS.includes(event.key);
      if (!isPan && !isZoom) return;
      // Не мешаем вводу в полях: поиск в селектах, хоткеи классов, настройки.
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      if (document.activeElement?.isContentEditable) return;
      if (!isFinite(viewStartRef.current) || !(viewDurationRef.current > 0)) return;

      event.preventDefault();

      if (isZoom) {
        // Те же множители, что у колеса, включая точную подстройку по Ctrl.
        // Якорь — центр окна: курсора, к которому можно было бы привязаться, нет.
        const multiplier = event.ctrlKey ? 1.1 : 1.25;
        performZoom(event.key === "ArrowUp" ? 1 / multiplier : multiplier, 0.5);
        return;
      }

      const step = viewDurationRef.current * (event.shiftKey ? 0.25 : 0.025);
      const delta = event.key === "ArrowRight" ? step : -step;
      // Функциональная форма: при удержании стрелки события приходят чаще,
      // чем React успевает ререндерить, и чтение из ref теряло бы шаги.
      setViewStart((prev) => prev + delta);
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [performZoom]);

  const newSegmentElement = useMemo(() => {
    if (!newSegment) return null;

    const start = toMs(newSegment.start_time);
    const end = videoTimeMs;
    const left = end < start
      ? ((end - viewStart) / viewDuration) * 100 + "%"
      : ((start - viewStart) / viewDuration) * 100 + "%";
    const width = end < start
      ? ((start - end) / viewDuration) * 100 + "%"
      : ((end - start) / viewDuration) * 100 + "%";

    return (
      <div
        id="newSection"
        style={{
          left,
          width,
          backgroundColor: newSegment.class?.class_color,
          border: "5px dashed black",
          position: "absolute",
          height: "100%",
          alignContent: "center",
          textOverflow: "clip",
          overflow: "hidden",
          whiteSpace: "nowrap",
          zIndex: "3",
        }}
      >
        &emsp;{newSegment.class?.class_name}
      </div>
    );
  }, [newSegment, videoTimeMs, viewStart, viewDuration]);

  // Маркер ведём от абсолютной позиции курсора, как и ручки сегментов.
  // Раньше копились дельты e.movementX, и маркер отставал от мыши:
  //   - movementX у объединённых событий pointermove браузеры суммируют не
  //     всегда, а местами отдают в физических пикселях вместо CSS;
  //   - любая потерянная дельта оставалась в позиции навсегда.
  // Теперь позиция маркера — функция текущего положения курсора.
  const videoTimeMsRef = useRef(videoTimeMs);
  videoTimeMsRef.current = videoTimeMs;
  // Смещение точки захвата от маркера во времени: ухватили квадрат не по
  // центру — маркер не прыгает центром под курсор.
  const playheadGrabOffsetRef = useRef(0);

  const cursorTime = (clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    return viewStartRef.current + ((clientX - rect.left) / rect.width) * viewDurationRef.current;
  };

  const handlePointerDownPlayhead = (e) => {
    if (e.button !== 0) return;
    isDraggingPlayhead.current = true;
    const at = cursorTime(e.clientX);
    playheadGrabOffsetRef.current = videoTimeMsRef.current != null ? videoTimeMsRef.current - at : 0;
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  const bgAutoScroll = useRef(false);
  const scrollMultiplier = useRef(null);

  const handlePointerMovePlayhead = (e) => {
    if (!isDraggingPlayhead.current) return;
    // Кнопка отпущена, а pointerup не дошёл — жест закончен, иначе маркер ехал
    // бы за курсором без нажатия (как было с ручками сегментов).
    if ((e.buttons & 1) === 0) {
      handlePointerUpPlayhead(e);
      return;
    }
    const containerBounds = containerRef.current.getBoundingClientRect()
    const positionPercent = (e.clientX - containerBounds.left) / containerBounds.width
    bgAutoScroll.current = positionPercent > 0.90 ? true : positionPercent < 0.10 ?  true : false;
    if (bgAutoScroll.current) {
      scrollMultiplier.current = positionPercent > 0.90 ? positionPercent - 0.90 : positionPercent < 0.10 ?  positionPercent - 0.10 : 0;
    }
    setVideoTimeMs(cursorTime(e.clientX) + playheadGrabOffsetRef.current);
  }

  useEffect(() => {
    let animationFrameId;

    const scrollLoop = () => {
      if (bgAutoScroll.current && scrollMultiplier.current) {
        // Мягкая скорость прокрутки
        const speedFactor = 0.015;
        const delta = scrollMultiplier.current * viewDurationRef.current * speedFactor;

        setViewStart((prev) => prev + delta);
        setVideoTimeMs((prev) => prev + delta);
      }
      // Запускаем следующий кадр синхронно с монитором (без перегрузки плеера)
      animationFrameId = requestAnimationFrame(scrollLoop);
    };

    animationFrameId = requestAnimationFrame(scrollLoop);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [setVideoTimeMs]);

  const handlePointerUpPlayhead = (e) => {
    isDraggingPlayhead.current = false;
    bgAutoScroll.current = false;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }

  const monoLetterWidth = useMemo(() => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = "1rem monospace";
    return context.measureText('ツ').width;
  }, [])

  const getDividerIndex = useCallback((W) => {
    if (viewStart === Infinity || !viewStart) return;
    const textWidth = monoLetterWidth; 
    let resultDivider = 0;
    for (; resultDivider < DIVIDERS.length; resultDivider++) {
      if (W - viewDuration / DIVIDERS[resultDivider] * textWidth * WIDTH_LETTERS[resultDivider] > 0) {
        break;
      }
    }
    return resultDivider;
  }, [viewStart, viewDuration])

  useEffect(() => {
    if (!containerRef.current) return;
    if (viewStart === Infinity) return;
    if (!viewStart) return;

    const W = containerRef.current.getBoundingClientRect().width;
    const result = [];
    const resultDivider = getDividerIndex(W);

    const offset = Math.round(DIVIDERS[resultDivider] * (Math.ceil(viewStart / DIVIDERS[resultDivider]) - viewStart / DIVIDERS[resultDivider]));
    for (let i = offset; viewStart + i < viewStart + viewDuration; i += DIVIDERS[resultDivider]) {
      result.push(
        <React.Fragment key={`time-mark-${i}`}>
          <div
          style={{
            "position": "absolute",
            "fontFamily": "monospace",
            "left": "calc(" + (i) / viewDuration * 100 + "% - " + WIDTH_LETTERS[resultDivider] / 4 * monoLetterWidth + "px)" 
          }}
          >
            {new Date(Math.round(viewStart + i) + timezoneOffset * 60 * 60 * 1000).toISOString().split("T")[1].slice(0, WIDTH_LETTERS[resultDivider])}
          </div>
          <div
            style={{
              "position": "absolute",
              "width": "3px",
              height: "0.67rem",
              backgroundColor: "#00000080",
              "left": (i) / viewDuration * 100 + "%" ,
              "top": "calc(100% - 0.67rem)"
            }}
          />
        </React.Fragment>
      )
    }
    setTimeMarks(result);
  }, [containerRef, viewStart, viewDuration, timezoneOffset])

  const isPanning = useRef(false);

  const handlePointerDown = (e) => {
    // Только основная кнопка: правый клик открывает меню, pointerup после него
    // не приходит, и шкала прокручивалась бы при простом наведении.
    if (e.button !== 0) return;
    isPanning.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  const handlePointerMove = (e) => {
    if (isPanning.current && (e.buttons & 1) === 0) {
      handlePointerUp(e);   // кнопка отпущена, а pointerup не дошёл
      return;
    }
    if (isPanning.current) {
      const timeDelta =
        e.movementX / containerRef.current.getBoundingClientRect().width * viewDurationRef.current;
      setViewStart(viewStartRef.current - timeDelta);
    }
  }

  const handlePointerUp = (e) => {
    isPanning.current = false;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }

  const handlePointerClick = (e) => {
    if (!containerRef || !videoTimeMs || e.detail !== 2) return;
    const bounds = e.target.getBoundingClientRect();
    setVideoTimeMs((e.clientX - bounds.left) / bounds.width * viewDurationRef.current + viewStartRef.current);
  }

  return (
    <div
      ref={containerRef}
      className="nn-markup-timeline d-flex flex-column h-100 w-100"
      style={{
        position: "relative",
        overflow: "hidden",
        userSelect: "none",
      }}
    >
      <div style={{display: "flex", zIndex:"2", justifyContent: "space-between", alignItems: "center"}}>
        <div style={{fontWeight: "bold", fontSize:"1.1rem", marginBottom:"0.5rem"}}>Таймлайн</div>
        <div className="text-muted" style={{ fontSize: "0.75rem", marginBottom: "0.5rem" }}>
          Пробел — воспроизведение видео
        </div>
      </div>
      
      <div
        className="nn-markup-playhead-container"
        style={{
          height: "2rem",
          width: "100%",
          position: "relative",
        }}
      >


        {timeMarks}
        <div
          className="nn-markup-playhead"
          style={{
            height: "2rem",
            width: "2rem",
            "backgroundColor": "black",
            position: "absolute",
            left: `calc(${(videoTimeMs - viewStart) / viewDuration * 100}% - 1rem)`,
            zIndex: "5",
          }}  
          onPointerDown={handlePointerDownPlayhead}
          onPointerMove={handlePointerMovePlayhead}
          onPointerUp={handlePointerUpPlayhead}
          onPointerCancel={handlePointerUpPlayhead}
          onLostPointerCapture={handlePointerUpPlayhead}
        >

        </div>
        
      </div>
      
      <div
        className="nn-markup-vertical-playhead"
        style={{
          zIndex: "4",
          height: "100%",
          width: "0.2rem",
          "backgroundColor": "black",
          position: "absolute",
          left: `calc(${(videoTimeMs - viewStart) / viewDuration * 100}% - 0.1rem)`
        }}  
      >

      </div>
      
      <NnMarkupSegments
        markups={markups}
        markupsHistory={markupsHistory}
        aiSessions={aiSessions}
        selectedClass={selectedClass}
        setSelectedClass={setSelectedClass}
        newSegmentElement={newSegmentElement}
        viewStart={viewStart}
        viewDuration={viewDuration}
        containerRef={containerRef}
        makeId={makeId}
        videoTimeMs={videoTimeMs}
        setVideoTimeMs={setVideoTimeMs}
        restorePlayheadAfterResize={clientSettings.restorePlayheadAfterResize}
        deleteSnapMode={clientSettings.deleteSnapMode || "middle"}
      />

      <NnMarkupGraph
        data={data}
        viewStart={viewStart}
        setViewStart={setViewStart}
        viewDuration={viewDuration}
        setViewDuration={setViewDuration}
        canvasResolutionDivider={clientSettings.canvasResolutionDivider}/>

      <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onLostPointerCapture={handlePointerUp}
          onClick={handlePointerClick}
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: "0",
          }}/>

    </div>
  )
}

const NnMarkupClasses = ({
  classes,
  setClasses,
  selectedClass,
  setSelectedClass,
  hotkeys,
  setHotkeys,
  vehicleType
}) => {
  const classesRef = useRef(classes);
  useEffect(() => {
    classesRef.current = classes;
  }, [classes]);

  const handleSelectClass = (mClass) => {
    setSelectedClass(mClass);
  };

  const changeClassColor = async (event, classId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `/api/imu-archives/activity-classes/${classId}/`,
        { new_color: event.target.value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Цвет изменён!");
      setClasses((prev) =>
        prev.map((el) =>
          el.id == classId ? { ...el, class_color: event.target.value } : el
        )
      );
    } catch (err) {
      toast.error("Ошибка сохранения цвета");
    }
  };

  const changeClassHotkey = (event, classId) => {
    event.preventDefault();

    const isClear = ["Backspace", "Delete", "Escape"].includes(event.code);
    if (!isClear && !event.code.includes("Key") && !event.code.includes("Digit")) return;

    if (isUndoRedoShortcut(event)) {
      toast.error("Ctrl/⌘+Z и Ctrl/⌘+Shift+Z заняты отменой и повтором");
      return;
    }

    // Ровно та же функция, что и при распознавании нажатия в handleSectionCreation,
    // поэтому записанное сочетание не может разойтись с ожидаемым.
    const hotkey = isClear ? "" : formatHotkey(event);

    const updated = { ...hotkeys };
    let stolenFrom = null;

    if (hotkey) {
      Object.keys(updated).forEach((otherId) => {
        if (otherId != classId && updated[otherId] === hotkey) {
          updated[otherId] = "";
          stolenFrom = classes.find((c) => c.id == otherId)?.class_name || otherId;
        }
      });
    }

    updated[classId] = hotkey;
    setHotkeys(updated);
    saveHotkeys(vehicleType, updated);

    if (!hotkey) toast.success("Горячая клавиша снята");
    else if (stolenFrom) toast.success(`Горячая клавиша переназначена, снята с «${stolenFrom}»`);
    else toast.success("Горячая клавиша обновлена!");
  };

  return (
    <div className="d-flex flex-wrap gap-2 align-items-center">
      {classes?.map((mClass) => {
        const isSelected = selectedClass?.id === mClass.id;
        const currentHk = hotkeys?.[mClass.id] || "";

        return (
          <div
            key={mClass.id}
            onClick={() => handleSelectClass(mClass)}
            className="d-flex align-items-center gap-1 px-2 py-1 rounded border shadow-sm"
            style={{
              cursor: "pointer",
              backgroundColor: isSelected ? "#cce5ff" : "#ffffff",
              borderColor: isSelected ? "#007bff" : "#dee2e6",
              fontSize: "0.8rem",
            }}
          >
            {/* Выбор цвета */}
            <input
              type="color"
              value={mClass.class_color || "#888888"}
              onChange={(e) => changeClassColor(e, mClass.id)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "18px",
                height: "18px",
                padding: 0,
                border: "none",
                borderRadius: "3px",
                cursor: "pointer",
              }}
            />

            {/* Название */}
            <span className="fw-bold text-truncate" style={{ maxWidth: "120px" }}>
              {mClass.class_name}
            </span>

            {/* Инпут горячей клавиши */}
            <input
              type="text"
              placeholder="Клавиша"
              title="Нажмите сочетание, чтобы назначить. Backspace или Delete — снять."
              value={currentHk}
              onKeyDown={(e) => changeClassHotkey(e, mClass.id)}
              onClick={(e) => e.stopPropagation()}
              readOnly
              className="text-center bg-light border rounded text-muted fw-bold"
              style={{ width: "65px", fontSize: "0.75rem", padding: "1px 2px" }}
            />
          </div>
        );
      })}
    </div>
  );
};

/**
 * Комбобокс поверх обычного текстового инпута: выглядит и ведёт себя как
 * закрытый select, но по клику/фокусу открывается список, а ввод с клавиатуры
 * сразу фильтрует его по подстроке — без стороннего select-компонента.
 *
 * options: [{ value, text, label? }] — text участвует в поиске и, если label
 * (JSX) не задан, отображается в строке списка как есть.
 */
const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = "-- Выбрать --",
  disabled = false,
  size,
  style,
  // Необязательная строка списка из самого ввода: freeOption(query)
  // возвращает { text } или null; выбор вызывает onFreeSelect(query).
  freeOption = null,
  onFreeSelect = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const itemRefs = useRef([]);

  const selected = useMemo(
    () => options.find((o) => o.value == value) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    const raw = query.trim();
    const q = raw.toLowerCase();
    const list = q ? options.filter((o) => o.text.toLowerCase().includes(q)) : options;
    const free = freeOption && raw ? freeOption(raw) : null;
    if (!free) return list;
    // Ввод совпал с вариантами — они первыми, строка ввода последней: Enter
    // по-прежнему выбирает найденное. Не совпал ни с чем — строка ввода первая.
    const freeRow = { ...free, value: "__free_input__", free: raw };
    return list.length ? [...list, freeRow] : [freeRow];
  }, [options, query, freeOption]);

  // Закрытие по клику вне контрола — список отрисован внутри того же wrapper,
  // поэтому клик по варианту сюда не попадёт.
  useEffect(() => {
    if (!isOpen) return;
    const onDocMouseDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [isOpen]);

  // Прокрутка внутри самого списка. scrollIntoView здесь не годится: он
  // двигает все прокручиваемые предки, включая страницу.
  //   "end"     — строка встаёт к нижнему краю списка, то есть вплотную над
  //               полем ввода (список раскрывается вверх);
  //   "nearest" — минимальный сдвиг, только если строка вышла за видимую часть.
  const scrollItemIntoList = (index, align) => {
    const list = listRef.current;
    const item = itemRefs.current[index];
    if (!list || !item) return;

    const top = item.offsetTop;
    const bottom = top + item.offsetHeight;

    if (align === "end") {
      list.scrollTop = Math.max(0, bottom - list.clientHeight);
    } else if (top < list.scrollTop) {
      list.scrollTop = top;
    } else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight;
    }
  };

  // При открытии — сразу к выбранному значению, чтобы оно стояло прямо над
  // полем поиска. Layout-эффект: прокрутка применяется до отрисовки, иначе
  // на кадр мелькнуло бы начало списка. Раньше прокрутка висела только на
  // смене highlighted и при повторном открытии на тот же индекс не срабатывала.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const selectedIndex = options.findIndex((o) => o.value == value);
    if (selectedIndex >= 0) scrollItemIntoList(selectedIndex, "end");
  }, [isOpen]);

  // Навигация стрелками и ввод — держим подсвеченную строку в поле зрения.
  useEffect(() => {
    if (!isOpen) return;
    scrollItemIntoList(highlighted, "nearest");
  }, [highlighted]);

  const openDropdown = () => {
    if (disabled || isOpen) return;
    setIsOpen(true);
    setQuery("");
    const selectedIndex = options.findIndex((o) => o.value == value);
    setHighlighted(selectedIndex >= 0 ? selectedIndex : 0);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setQuery("");
  };

  const commit = (option) => {
    if (option.free != null) {
      closeDropdown();
      inputRef.current?.blur();
      onFreeSelect?.(option.free);
      return;
    }
    // Отдаём строку, как это делает нативный <select>: e.target.value у него
    // всегда строка. Обработчики на это опираются — например, поиск архива
    // сравнивает a.id.toString() === archId строгим равенством.
    onChange(String(option.value));
    closeDropdown();
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        openDropdown();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const option = filtered[highlighted];
      if (option) commit(option);
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeDropdown();
    }
  };

  const inputValue = isOpen ? query : selected?.text ?? "";

  return (
    <div ref={wrapperRef} className="position-relative w-100" style={{ minWidth: 0, ...style }}>
      <input
        ref={inputRef}
        type="text"
        className={"form-control" + (size ? ` form-control-${size}` : "")}
        placeholder={isOpen ? "Поиск..." : placeholder}
        value={inputValue}
        disabled={disabled}
        onFocus={openDropdown}
        onClick={openDropdown}
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlighted(0);
        }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {isOpen && (
        <div
          ref={listRef}
          className="border rounded bg-white shadow-sm"
          style={{
            position: "absolute",
            // Контрол внизу страницы — список раскрывается вверх, иначе
            // упирается в нижний край экрана и его не видно.
            bottom: "calc(100% + 2px)",
            left: 0,
            right: 0,
            maxHeight: "90vh",
            overflowY: "auto",
            zIndex: 1050,
          }}
        >
          {filtered.length === 0 ? (
            <div className="px-2 py-1 text-muted small">Ничего не найдено</div>
          ) : (
            filtered.map((option, index) => (
              <div
                key={option.value}
                ref={(el) => (itemRefs.current[index] = el)}
                className={
                  "px-2 py-1 small" +
                  (index === highlighted ? " bg-primary text-white" : "") +
                  (option.value == value && index !== highlighted ? " fw-bold" : "")
                }
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setHighlighted(index)}
                onClick={() => commit(option)}
              >
                {option.label ?? option.text}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const ImuViewerPage = () => {
  const location = useLocation();
  const isSuperuser = localStorage.getItem("is_superuser") === "true";

  const [devices, setDevices] = useState([]);
  const [devicesReady, setDevicesReady] = useState(false);
  // Обработчики, начатые до перерисовки (открытие архива по номеру), читают
  // список техники и выбранную технику отсюда, а не из своего замыкания.
  const devicesRef = useRef(devices);
  devicesRef.current = devices;
  const [timezoneOffset, setTimezoneOffset] = useState(0);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState("");
  const vehicleTypeOptions = useMemo(
    () => vehicleTypes.map((element) => ({ value: element.id, text: element.name })),
    [vehicleTypes]
  );
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const selectedDeviceIdRef = useRef(null);
  const archiveSearchIdRef = useRef(0);

  // Необязательный фильтр техники по организации — как на странице разметки
  // видео. "" — все организации.
  const [orgFilter, setOrgFilter] = useState("");

  const uniqueOrganizations = useMemo(() => {
    if (!isSuperuser) return [];
    const orgs = devices.map((d) => d.organization_name).filter(Boolean);
    return [...new Set(orgs)].sort();
  }, [devices, isSuperuser]);

  const organizationOptions = useMemo(
    () => [
      { value: "", text: "-- Все организации --" },
      ...uniqueOrganizations.map((org) => ({ value: org, text: org })),
    ],
    [uniqueOrganizations]
  );

  const deviceOptions = useMemo(
    () =>
      devices
        .filter((d) => !orgFilter || d.organization_name === orgFilter)
        .map((d) => ({ value: d.id, text: d.name })),
    [devices, orgFilter]
  );

  const [imuArchives, setImuArchives] = useState([]);
  const [datasetSplit, setDatasetSplit] = useState(null);
  const [selectedArchiveId, setSelectedArchiveId] = useState(null);
  const selectedArchiveIdRef = useRef(selectedArchiveId);
  useEffect(() => {
    selectedArchiveIdRef.current = selectedArchiveId;
  }, [selectedArchiveId])

  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);

  const [sensorData, setSensorData] = useState(null)
  
  // Кадры видео (номер -> время с сервера) и они же во времени IMU: ко времени
  // кадра прибавлен сдвиг видео. Всё ниже работает с frameMapping.
  const [rawFrameMapping, setFrameMapping] = useState([]);
  const [videoOffsetMs, setVideoOffsetMs] = useState(0);
  const frameMapping = useMemo(
    () =>
      videoOffsetMs
        ? rawFrameMapping.map((element) => ({ f: element.f, t: element.t + videoOffsetMs }))
        : rawFrameMapping,
    [rawFrameMapping, videoOffsetMs]
  );
  const frameMappingRef = useRef(frameMapping);

  useEffect(() => {
    frameMappingRef.current = frameMapping;
  }, [frameMapping])

  const telemetry = useLabelingTelemetry();

  // Разметка вместе с историей правок — см. markupHistoryReducer.
  const [markupHistory, dispatchMarkupHistory] = useReducer(
    markupHistoryReducer,
    INITIAL_MARKUP_HISTORY
  );
  const markups = markupHistory.present;
  const markupHistoryRef = useRef(markupHistory);
  markupHistoryRef.current = markupHistory;

  // Единственный вход для изменения разметки. Обычного setMarkups больше нет
  // намеренно: каждое место должно явно решить, правка это пользователя
  // (попадает в отмену) или служебное обновление (не попадает).
  // Объект стабилен — его спокойно держат обработчики, повешенные один раз.
  const markupsHistory = useMemo(
    () => ({
      // action — имя действия для телеметрии, extra — подробности к нему.
      edit: (updater, action = "edit", extra) => {
        telemetry.track(action, extra);
        dispatchMarkupHistory({ type: "edit", updater });
      },
      silent: (updater) => dispatchMarkupHistory({ type: "silent", updater }),
      checkpoint: (action, extra) => {
        if (action) telemetry.track(action, extra);
        dispatchMarkupHistory({ type: "checkpoint" });
      },
      dropCheckpointIfUnchanged: () => dispatchMarkupHistory({ type: "dropCheckpointIfUnchanged" }),
      reset: (value) => dispatchMarkupHistory({ type: "reset", value }),
      undo: () => {
        if (!markupHistoryRef.current.past.length) return;
        telemetry.track("undo");
        dispatchMarkupHistory({ type: "undo" });
      },
      redo: () => {
        if (!markupHistoryRef.current.future.length) return;
        telemetry.track("redo");
        dispatchMarkupHistory({ type: "redo" });
      },
    }),
    [telemetry]
  );

  // Итог разметки (сегменты, покрытие) телеметрия снимает сама в момент
  // снимка или завершения сессии — ей нужен доступ к актуальной разметке.
  useEffect(() => {
    telemetry.setMarkupsSource(() => markupHistoryRef.current.present);
  }, [telemetry]);

  const [initialMarkups, setInitialMarkups] = useState(markups);
  const [draftModalVisible, setDraftModalVisible] = useState(false);
  // Черновик держим здесь, а не перечитываем из localStorage при нажатии:
  // так восстановление не зависит от того, что успело записаться после.
  const pendingDraftRef = useRef(null);
  // Архив, разметка которого уже пришла с сервера. Пока он не совпадает
  // с выбранным, черновик не пишется — иначе старая разметка ложится под новый ключ.
  const loadedArchiveIdRef = useRef(null);

  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [clientSettings, setClientSettings] = useState(loadClientSettings);

  const updateClientSettings = useCallback((patch) => {
    setClientSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(CLIENT_SETTINGS_KEY, JSON.stringify(clientSettings));
    } catch {
      // приватный режим — настройки просто не переживут перезагрузку
    }
  }, [clientSettings]);
  const [aiSessions, setAiSessions] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [hotkeys, setHotkeys] = useState({});
  const hotkeysRef = useRef(hotkeys);
  useEffect(() => {
    hotkeysRef.current = hotkeys;
  }, [hotkeys])
  const availableClassesRef = useRef(null);
  useEffect(() => {
    availableClassesRef.current = availableClasses;
  }, [availableClasses])
  const [selectedClass, setSelectedClass] = useState(null);

  const [isLoading, setIsLoading] = useState(true);

  const makeId = useCallback(() => {
    const maxId = (markupsRef.current || []).reduce(
      (acc, el) => Math.max(acc, Number(el.id) || 0),
      -1
    );
    const next = Math.max(newIdRef.current ?? 0, maxId + 1);
    newIdRef.current = next + 1;
    return next;
  }, []);

  useEffect(() => {
    if (!isSuperuser) {
      window.location.href = "/dashboard";
      return;
    }
    let active = true;
    setIsLoading(true);
    const fetchDevices = async () => {
      try {
        const token = localStorage.getItem("token");
        const list = await fetchApiList("/api/devices/", {
          headers: { Authorization: `Bearer ${token}` },
          params: { imu: true },
        });
        if (!active) return false;
        devicesRef.current = list;
        setDevices(list);
        setDevicesReady(true);
        return true;
      } catch (err) {
        if (active) toast.error("Ошибка загрузки устройств");
        return false;
      } finally {
        if (active) setIsLoading(false);
      }
    };
    fetchDevices();
    return () => {
      active = false;
      archiveSearchIdRef.current += 1;
    };
  }, [isSuperuser]);

  // Также реагируем на переход по ссылке с другим ?archive без перемонтирования.
  useEffect(() => {
    if (!devicesReady) return;
    const archId = archiveIdFromUrl();
    if (archId) openArchiveById(archId);
  }, [devicesReady, location.search]);

  useEffect(() => {
    const new_offset = devices.find((device) => device.id == selectedDeviceId)?.timezone_offset + 3;
    setTimezoneOffset(new_offset || 0)
  }, [selectedDeviceId])

  const handleDeviceChange = async (e, preloadedArchives = null) => {
    archiveSearchIdRef.current += 1;
    const dId = e.target.value;
    // Смена техники выгружает архив — заход на него закончен.
    telemetry.endSession("device_change");
    selectedDeviceIdRef.current = dId;
    setSelectedDeviceId(dId);
    selectedArchiveIdRef.current = "";
    setSelectedArchiveId("");
    writeArchiveIdToUrl(null);
    setImuArchives([]);
    setSelectedVideoUrl(null);
    setSensorData(null);
    setFrameMapping([]);

    // Список архивов ещё и возвращается: открытию по номеру он нужен сразу,
    // не дожидаясь перерисовки.
    if (!dId) {
      setIsLoading(false);
      return [];
    }
    if (preloadedArchives) {
      setImuArchives(preloadedArchives);
      return preloadedArchives;
    }
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const archives = await fetchApiList(`/api/devices/${dId}/imu-archives/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setImuArchives(archives);
      return archives;
    } catch (err) {
      toast.error("Ошибка загрузки архивов");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrgChange = (org) => {
    setOrgFilter(org);
    // Выбранную технику сбрасываем, только если она не входит в новую
    // организацию. Безусловный сброс, как на странице разметки видео, здесь
    // выгружал бы открытый архив вместе с разметкой при каждом касании фильтра.
    if (!org || !selectedDeviceId) return;
    const current = devices.find((d) => d.id == selectedDeviceId);
    if (current && current.organization_name !== org) {
      handleDeviceChange({ target: { value: "" } });
    }
  };

  // archivesList — архивы выбранной техники; по умолчанию из состояния, при
  // открытии по номеру — только что загруженные.
  const handleArchiveChange = async (e, archivesList = imuArchives) => {
    archiveSearchIdRef.current += 1;
    const archId = String(e.target.value);
    selectedArchiveIdRef.current = archId;
    setSelectedArchiveId(archId);
    writeArchiveIdToUrl(archId);
    setVideoOffsetMs(archId ? loadVideoOffset(archId, selectedDeviceIdRef.current) : 0);
    setSensorData(null);
    setSelectedVideoUrl(null);
    setFrameMapping([]);
    setDraftModalVisible(false);

    // Пока новая разметка не пришла, запись черновиков выключена, а старая
    // разметка убрана из состояния: иначе она успевала лечь под новый ключ.
    // История правок — тоже с нуля: отмена не должна уводить в чужой архив.
    telemetry.endSession("archive_change");
    loadedArchiveIdRef.current = null;
    pendingDraftRef.current = null;
    markupsHistory.reset([]);
    setInitialMarkups([]);
    setAiSessions([]);

    if (!archId) {
      setIsLoading(false);
      return;
    }
    // Спиннер включаем только после того, как архив найден: раньше ранний
    // выход ниже оставлял isLoading навсегда взведённым.
    const selectedArch = archivesList.find((a) => a.id.toString() === archId);
    if (!selectedArch) {
      setIsLoading(false);
      toast.error(`Архива ${archId} нет в списке архивов этой техники`);
      return;
    }
    setIsLoading(true);

    try {
      const token = localStorage.getItem("token");

      const currentType = await axios.get(
        `/api/imu-archives/imu-veh-type-relation/${archId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      
      const markupRes = await axios.get(
        `/api/imu-archives/${archId}/markup/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const vehicleTypes = await axios.get(
        `/api/imu-archives/vehicle_types/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Защита от пустого или некорректного значения типа техники
      setSelectedVehicleType(currentType.data?.id || "");
      
      setVehicleTypes(vehicleTypes.data.vehicle_types);
      setDatasetSplit(markupRes.data.dataset_split);
      setInitialMarkups(markupRes.data.markups);
      markupsHistory.reset(markupRes.data.markups);
      setAiSessions(markupRes.data.markups);
      loadedArchiveIdRef.current = archId;

      // Заход на архив начался: дальше считаем активное время и действия.
      // Длительность записи допишется, когда распарсится CSV.
      const archiveDevice = devicesRef.current.find((d) => d.id == selectedDeviceIdRef.current);
      telemetry.startSession({
        archive: {
          archive_id: Number(archId),
          device_id: selectedDeviceIdRef.current ? Number(selectedDeviceIdRef.current) : null,
          vehicle_type_id: currentType.data?.id ?? null,
          dataset_split: markupRes.data.dataset_split ?? null,
          folder_path: selectedArch.folder_path ?? null,
          archive_timestamp: selectedArch.timestamp ?? null,
          had_markup: Boolean(selectedArch.has_markup),
          initial_segments: markupRes.data.markups?.length ?? 0,
          available_classes: markupRes.data.available_classes?.length ?? 0,
          duration_ms: null,
        },
        context: {
          organization: archiveDevice?.organization_name ?? null,
          timezone_offset_h: (archiveDevice?.timezone_offset ?? 0) + 3,
        },
        settings: clientSettings,
      });

      // Раскладка привязана к типу техники. Раньше читали из ключа "hotkeys",
      // в который никто не писал, поэтому сочетания терялись при каждой загрузке.
      const vehicleTypeId = currentType.data?.id || "";
      const nextHotkeys = loadHotkeys(vehicleTypeId, markupRes.data.available_classes);
      setHotkeys(nextHotkeys);
      saveHotkeys(vehicleTypeId, nextHotkeys);

      setAvailableClasses(markupRes.data.available_classes);

      // Черновик предлагаем только теперь, когда серверная разметка уже в руках,
      // и только если он от неё действительно отличается.
      const rawDraft = localStorage.getItem(draftStorageKey(archId));
      if (rawDraft) {
        try {
          const draft = JSON.parse(rawDraft);
          if (markupsFingerprint(draft) !== markupsFingerprint(markupRes.data.markups)) {
            pendingDraftRef.current = draft;
            setDraftModalVisible(true);
          } else {
            localStorage.removeItem(draftStorageKey(archId));
          }
        } catch {
          localStorage.removeItem(draftStorageKey(archId));
        }
      }
    }
    catch (err) {
      console.log(
        err,
      );
    }

    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/imu-archives/${archId}/data/`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const zip = await JSZip.loadAsync(res.data);
      const csv_file = zip.file("imu_data.csv");
      const csv_data = await csv_file.async('string');
      parseIMUCSV(csv_data);

    } 
    catch (err) {
      toast.error("Ошибка чтения CSV данных");
    }
    finally {
      setIsLoading(false);
    }
  };

  // Поиск по номеру использует те же списки, что и обычные селекторы.
  // До находки не сбрасываем текущий архив, фильтр и URL.
  const openArchiveById = async (rawId) => {
    const archId = String(rawId).trim().replace(/^0+(?=\d)/, "");
    if (!/^\d+$/.test(archId)) return;
    if (archId === String(selectedArchiveIdRef.current)) return;
    const requestId = ++archiveSearchIdRef.current;
    const isCurrent = () => requestId === archiveSearchIdRef.current;
    setIsLoading(true);
    try {
      const currentDevice = devicesRef.current.find((d) => String(d.id) === String(selectedDeviceIdRef.current));
      let found = currentDevice && imuArchives.some((a) => String(a.id) === archId)
        ? { device: currentDevice, archives: imuArchives }
        : null;
      if (!found) {
        const token = localStorage.getItem("token");
        // Сначала выбранная техника; остальная — независимо от фильтра организации.
        const ordered = currentDevice
          ? [currentDevice, ...devicesRef.current.filter((d) => d.id !== currentDevice.id)]
          : devicesRef.current;
        found = await findArchiveDevice(archId, ordered, (id) =>
          fetchApiList(`/api/devices/${id}/imu-archives/`, {
            headers: { Authorization: `Bearer ${token}` },
          }), isCurrent);
      }
      if (!isCurrent()) return;
      if (!found) {
        toast.error(`Архив ${archId} не найден среди доступной техники`);
        return;
      }
      const { device, archives } = found;
      setOrgFilter((prev) => (prev && prev !== device.organization_name ? "" : prev));
      // Список уже загружен поиском: повторно его не запрашиваем.
      await handleDeviceChange({ target: { value: String(device.id) } }, archives);
      await handleArchiveChange({ target: { value: archId } }, archives);
    } catch {
      if (isCurrent()) toast.error("Не удалось открыть архив: ошибка загрузки списков");
    } finally {
      // handleDeviceChange/handleArchiveChange уже завершили поиск и управляют
      // своими спиннерами; отменённый поиск не трогает состояние нового действия.
      if (isCurrent()) setIsLoading(false);
    }
  };

  // Длительность записи известна только после разбора CSV — дописываем её
  // в текущую сессию: от неё считается доля размеченного.
  useEffect(() => {
    if (!sensorData?.minTimeMs || !sensorData?.maxTimeMs) return;
    telemetry.updateArchive(
      {
        duration_ms: sensorData.maxTimeMs - sensorData.minTimeMs,
        sensor_points:
          (sensorData.accel?.length || 0) +
          (sensorData.gyro?.length || 0) +
          (sensorData.magnet?.length || 0),
      },
      "data_loaded"
    );
  }, [sensorData, telemetry]);

  useEffect(() => {
    if (!sensorData?.minTimeMs)
      return;
    const token = localStorage.getItem("token");

    const middleTimeIso = dayjs((sensorData.maxTimeMs - sensorData.minTimeMs) / 2 + sensorData.minTimeMs)
      .toISOString();
    const tsEncoded = encodeURIComponent(middleTimeIso);
    (async () => {
      try {
        const vidRes = await axios.get(
          `/api/devices/${selectedDeviceId}/video-frame/?timestamp=${tsEncoded}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const frameMappingRes = await axios.get(
          `/api/folders/${vidRes.data.folder_id}/markup/`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setSelectedVideoUrl(vidRes.data.video_url);
        setFrameMapping(frameMappingRes.data.frames_mapping
          .map((elem) => {
            return {
              f: elem.f,
              t: new Date(elem.t).getTime()
            }
          }) || []);
      }
      catch (err) {
        toast.error(err)
      }
    })();
    
  }, [sensorData]);

  const parseIMUCSV = (csvText) => {
    const GYRO_TYPE = 16, ACCEL_TYPE = 35, MAGNET_TYPE = 11;
    const lines = csvText.split("\n");
    const accel = [];
    const gyro = [];
    const rawMagnet = [];
    let minTime = Infinity, maxTime = -Infinity;

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const parts = line.split(",");
      if (parts.length < 6) continue;

      const tsUnixMs = parseInt(parts[0], 10);
      const type = parseInt(parts[2], 10);
      const x = parseFloat(parts[3]);
      const y = parseFloat(parts[4]);
      const z = parseFloat(parts[5]);

      minTime = Math.min(minTime, tsUnixMs);
      maxTime = Math.max(maxTime, tsUnixMs);
      
      if (type === ACCEL_TYPE) accel.push({ t: tsUnixMs, x, y, z });
      if (type === GYRO_TYPE) gyro.push({ t: tsUnixMs, x, y, z });
      if (type === MAGNET_TYPE) rawMagnet.push({ t: tsUnixMs, x, y, z });
    }

    const magnet = [];
    let prevYaw = null;
    let yawOffset = 0;

    let prevPitch = null;
    let pitchOffset = 0;

    let prevRoll = null;
    let rollOffset = 0;

    for (let i = 0; i < rawMagnet.length; i++) {
      let { t, x, y, z } = rawMagnet[i];

      const sumSq = x * x + y * y + z * z;
      const w = sumSq <= 1.0 ? Math.sqrt(Math.max(0, 1.0 - sumSq)) : 0;

      let yaw = Math.atan2(2.0 * (w * z + x * y), 1.0 - 2.0 * (y * y + z * z)) * (180 / Math.PI);
      
      let sinp = 2.0 * (w * y - z * x);
      let pitch = (Math.abs(sinp) >= 1 ? Math.sign(sinp) * 90 : Math.asin(sinp) * (180 / Math.PI));

      let roll = Math.atan2(2.0 * (w * x + y * z), 1.0 - 2.0 * (x * x + y * y)) * (180 / Math.PI);

      if (isNaN(yaw) || sumSq > 1.5) {
        yaw = Math.atan2(y, x) * (180 / Math.PI);
        pitch = Math.atan2(x, Math.sqrt(y * y + z * z)) * (180 / Math.PI);
        roll = Math.atan2(z, Math.sqrt(x * x + y * y)) * (180 / Math.PI);
      }

      if (prevYaw !== null) {
        let diff = yaw - prevYaw;
        if (diff > 180) yawOffset -= 360;
        else if (diff < -180) yawOffset += 360;
      }
      prevYaw = yaw;
      const unwrappedYaw = yaw + yawOffset;

      if (prevPitch !== null) {
        let diff = pitch - prevPitch;
        if (diff > 180) pitchOffset -= 360;
        else if (diff < -180) pitchOffset += 360;
      }
      prevPitch = pitch;
      const unwrappedPitch = pitch + pitchOffset;

      if (prevRoll !== null) {
        let diff = roll - prevRoll;
        if (diff > 180) rollOffset -= 360;
        else if (diff < -180) rollOffset += 360;
      }
      prevRoll = roll;
      const unwrappedRoll = roll + rollOffset;

      magnet.push({
        t,
        x: unwrappedYaw,  
        y: unwrappedPitch, 
        z: unwrappedRoll,  
      });
    }

    setSensorData({ 
      accel: accel, 
      gyro: gyro, 
      magnet: magnet, 
      minTimeMs: minTime, 
      maxTimeMs: maxTime 
    });
    
    toast.success(`Данные загружены! Точек: ${accel.length + gyro.length}`);
  };

  // Черновик пишем только для полностью загруженного архива и только когда
  // разметка реально отличается от серверной. Отпечаток не учитывает цвет
  // класса, поэтому перекраска класса черновиком не считается.
  useEffect(() => {
    if (!selectedArchiveId) return;
    if (loadedArchiveIdRef.current !== selectedArchiveId) return;
    // Пока вопрос о черновике открыт, ключ не трогаем: иначе закрытие модалки
    // крестиком стирало бы черновик, о котором пользователь ещё не решил.
    if (draftModalVisible) return;

    const key = draftStorageKey(selectedArchiveId);
    if (markupsFingerprint(initialMarkups) === markupsFingerprint(markups)) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(markups || []));
  }, [markups, initialMarkups, selectedArchiveId, draftModalVisible])

  const selectedVehicleTypeRef = useRef(selectedVehicleType)
  useEffect(() => {
    selectedVehicleTypeRef.current = selectedVehicleType;
  }, [selectedVehicleType])

  const markupsRef = useRef(markups)
  useEffect(() => {
    markupsRef.current = markups;
  }, [markups])

  const handleVehicleTypeChange = async (event) => {
    if (!selectedVehicleTypeRef.current) return;
    console.log(markupsRef.current)
    if (markupsRef.current?.length != 0) {
      toast.error("Удалите разметку перед сменой типа техники!");
      return;
    };
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `/api/imu-archives/imu-veh-type-relation/${selectedArchiveIdRef.current}/${selectedVehicleTypeRef.current}/`,
        null,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const markupRes = await axios.get(
        `/api/imu-archives/${selectedArchiveIdRef.current}/markup/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      // Раньше сюда мог попасть null от JSON.parse, и обработчик хоткеев падал.
      const nextHotkeys = loadHotkeys(selectedVehicleTypeRef.current, markupRes.data.available_classes);
      setHotkeys(nextHotkeys);
      saveHotkeys(selectedVehicleTypeRef.current, nextHotkeys);

      setInitialMarkups(markupRes.data.markups);
      markupsHistory.reset(markupRes.data.markups);
      setAiSessions(markupRes.data.markups);
      setAvailableClasses(markupRes.data.available_classes);
      telemetry.track("vehicle_type_change", { vehicle_type_id: selectedVehicleTypeRef.current });
      telemetry.updateArchive({ vehicle_type_id: selectedVehicleTypeRef.current });
      toast.success("Разметка успешно сохранена!");
    } catch (err) {
      toast.error("Ошибка сохранения разметки");
    };
  }

  const handleSplitChange = async (event) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `/api/imu-archives/${selectedArchiveIdRef.current}/markup/`,
        {
          dataset_split: datasetSplit,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Сплит " + datasetSplit + " успешно сохранён!");
    } catch (err) {
      toast.error("Ошибка сохранения разметки");
    };
  }

  const [currentAbsoluteTimeMs, setCurrentAbsoluteTimeMs] = useState(null);
  // Источник истины для обработчиков — ref, и пишут в него сами писатели
  // (handleProgress, seekToAbsoluteTime, начальная установка ниже).
  //
  // Раньше ref ещё и зеркалился из состояния эффектом. Эффект выполняется
  // после коммита — уже когда следующий pointermove успел записать в ref более
  // свежее значение, — и откатывал ref назад. Следующий шаг перетаскивания
  // считался от устаревшей позиции: маркер отставал от мыши и дёргался.
  const currentAbsoluteTimeMsRef = useRef(currentAbsoluteTimeMs);

  useEffect(() => {
    if (frameMapping.length  && !currentAbsoluteTimeMs) {
      currentAbsoluteTimeMsRef.current = frameMapping[0].t;
      setCurrentAbsoluteTimeMs(frameMapping[0].t)
    }
  }, [currentAbsoluteTimeMs, frameMapping])

  const [seekToTime, setSeekToTime] = useState(null);
  const [seekTriggerId, setSeekTriggerId] = useState(0);
  const playerRef = useRef(null);

  // Заказанная перемотка доходит до плеера не мгновенно: какое-то время он
  // продолжает отдавать прогресс со старой позиции. Пока окно не истекло,
  // прогресс игнорируем — иначе маркер отскакивает назад, и во время
  // воспроизведения перемотка выглядит непроходящей.
  const SEEK_SETTLE_MS = 400;
  const lastUserSeekAtRef = useRef(0);
  // Кадр, на который мы сами последний раз перемотали видео. Первый прогресс
  // с этим кадром — эхо нашей же перемотки, а не движение видео. Если его
  // принять, маркер перескакивает с точной позиции курсора на начало кадра:
  // при медленной перемотке эхо приходит позже окна SEEK_SETTLE_MS, и маркер
  // дёргался уже после того, как мышь остановилась.
  const lastSeekFrameRef = useRef(null);

  // Видео двигает маркер, но само себя не перематывает.
  const handleProgress = (progressData) => {
    if (performance.now() - lastUserSeekAtRef.current < SEEK_SETTLE_MS) return;
    if (!frameMappingRef.current.length) return;

    const vSec =
      typeof progressData === "number"
        ? progressData
        : progressData?.playedSeconds || 0;
    const frame = Math.round(vSec * VIDEO_FPS);
    if (frame >= frameMappingRef.current.length) return;

    if (frame === lastSeekFrameRef.current) {
      lastSeekFrameRef.current = null;
      return;
    }
    lastSeekFrameRef.current = null;

    const t = frameMappingRef.current[frame].t;
    if (t !== currentAbsoluteTimeMsRef.current) telemetry.notePlayback();
    currentAbsoluteTimeMsRef.current = t;
    setCurrentAbsoluteTimeMs(t);
  };

  // Таймлайн двигает маркер И перематывает видео.
  //
  // Перемотка — команда, а не реакция на изменение состояния. Прежняя схема
  // с парой флагов jumpFromVideo/jumpFromTimeline глотала перемотку, если во
  // время воспроизведения кадр прогресса успевал прийти между действием
  // пользователя и эффектом: эффект видел взведённый jumpFromVideo, считал
  // изменение «пришедшим от видео» и молча выходил.
  const seekToAbsoluteTime = useCallback((valueOrUpdater) => {
    const prev = currentAbsoluteTimeMsRef.current;
    const next = typeof valueOrUpdater === "function" ? valueOrUpdater(prev) : valueOrUpdater;
    if (next == null || !isFinite(next)) return;

    // В ref пишем сразу: серия вызовов внутри одного кадра (перетаскивание
    // маркера, автопрокрутка у края) должна накапливаться, а не читать
    // значение, которое React ещё не успел закоммитить.
    currentAbsoluteTimeMsRef.current = next;
    setCurrentAbsoluteTimeMs(next);

    const frames = frameMappingRef.current;
    if (!frames.length) return;

    lastUserSeekAtRef.current = performance.now();
    const index = frames.findIndex(
      (element, i, array) => element.t - next > 0 || i === array.length - 1
    );
    lastSeekFrameRef.current = index;
    telemetry.count("seek");
    setSeekToTime(index / VIDEO_FPS);
    // Счётчик, а не Date.now(): два вызова в одну миллисекунду дали бы
    // одинаковый id, и эффект перемотки внутри плеера не сработал бы.
    setSeekTriggerId((n) => n + 1);
  }, []);

  // Сдвиг изменили — маркер остаётся на своём времени IMU, видео встаёт на
  // кадр, который теперь этому времени соответствует. frameMappingRef к этому
  // моменту уже обновлён: его эффект объявлен выше и выполняется раньше.
  const appliedOffsetRef = useRef(videoOffsetMs);
  useEffect(() => {
    if (appliedOffsetRef.current === videoOffsetMs) return;
    appliedOffsetRef.current = videoOffsetMs;
    if (!frameMappingRef.current.length || currentAbsoluteTimeMsRef.current == null) return;
    seekToAbsoluteTime(currentAbsoluteTimeMsRef.current);
  }, [videoOffsetMs, seekToAbsoluteTime]);

  // Поле ввода сдвига — своя строка, чтобы набор "-" или "1." не сбрасывался
  // форматированием; применяется по Enter и при уходе из поля.
  const [offsetInput, setOffsetInput] = useState("0.0");
  useEffect(() => {
    setOffsetInput((videoOffsetMs / 1000).toFixed(1));
  }, [videoOffsetMs]);

  const changeVideoOffset = (ms) => {
    if (!Number.isFinite(ms)) {
      setOffsetInput((videoOffsetMs / 1000).toFixed(1));
      return;
    }
    const next = Math.round(ms / 100) * 100;      // шаг 0.1 с
    if (next === videoOffsetMs) return;
    setVideoOffsetMs(next);
    if (selectedArchiveIdRef.current) {
      saveVideoOffset(selectedArchiveIdRef.current, selectedDeviceIdRef.current, next);
    }
    telemetry.track("video_offset", { offset_ms: next });
  };

  // Подтягиваем класс сегмента из справочника по id. Предсказание возвращает
  // у класса только id, поэтому имя подставляем здесь же вместе с цветом —
  // иначе class_name появлялся бы лишь после сохранения и перезахода.
  //
  // Функциональная форма обязательна: эффект срабатывает уже после того, как
  // перетаскивание успело поставить в очередь следующий кадр, и запись готового
  // массива из своего замыкания откатила бы границу на кадр назад. Обновление
  // считается от prev; если менять нечего, возвращаем prev и React не
  // перерисовывает.
  // Служебное обновление — в историю отмены не попадает.
  useEffect(() => {
    if (!markups?.length || !availableClasses.length) return;

    markupsHistory.silent((prev) => {
      let changed = false;
      const next = prev.map((element) => {
        const source = availableClasses.find((mClass) => mClass.id == element.class.id);
        if (!source) return element;

        const newColor = source.class_color ?? element.class.class_color;
        const newName = source.class_name ?? element.class.class_name;
        if (newColor === element.class.class_color && newName === element.class.class_name) {
          return element;
        }

        changed = true;
        return {
          ...element,
          class: { ...element.class, class_color: newColor, class_name: newName },
        };
      });
      return changed ? next : prev;
    });
  }, [availableClasses, markups]);

  useEffect(() => {
    if (!aiSessions?.length || !availableClasses.length) return;

    let changed = false;
    const next = aiSessions.map((element) => {
      const newColor = availableClasses.find((mClass) => mClass.id == element.class.id)?.class_color;
      if (!newColor || newColor === element.class.class_color) return element;
      changed = true;
      return { ...element, class: { ...element.class, class_color: newColor } };
    });
    if (changed) setAiSessions(next);
  }, [availableClasses, aiSessions]);

  const [newSegment, setNewSegment] = useState(null);
  const newIdRef = useRef(null);
  useEffect(() => {
    newIdRef.current = markups.reduce(
    (accumulator, element) => 
      Math.max(accumulator, element.id)
    , -1) 
    + 1;
  }, [availableClasses])
  const newSegmentRef = useRef(newSegment);
  useEffect(() => {
    newSegmentRef.current = newSegment;
  }, [newSegment])

  const handleSectionCreation = (event) => {
    if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement.tagName)) return;
    if (!event.code.includes("Key") && !event.code.includes("Digit")) return;
    // Отмена и повтор — не хоткей класса, даже если такое сочетание успели
    // назначить до появления отмены.
    if (isUndoRedoShortcut(event)) return;

    // Та же функция, что и при назначении сочетания в NnMarkupClasses.
    const hotkey = formatHotkey(event);

    // Теперь класс нужен и при закрытии сегмента, поэтому неизвестные
    // сочетания игнорируются в обеих ветках (раньше закрывала любая клавиша).
    const hkEntry = Object.entries(hotkeysRef.current || {}).find(
      ([, value]) => value && value === hotkey
    );
    if (!hkEntry) return;
    const mClass = availableClassesRef.current?.find((element) => element.id == hkEntry[0]);
    if (!mClass) return;

    const now = currentAbsoluteTimeMsRef.current;
    if (now == null) return;

    const pending = newSegmentRef.current;

    // Ничего не тянется — начинаем новый сегмент.
    if (!pending) {
      telemetry.track("segment_start", { class_id: mClass.id });
      setNewSegment({ start_time: toIso(now), class: mClass, id: makeId() });
      return;
    }

    // Закрываем текущий по времени нажатия и вписываем его в разметку:
    // накрытые сегменты удаляются, задетые обрезаются, охваченный режется надвое.
    const pendingStart = toMs(pending.start_time);
    const committed = {
      ...pending,
      start_time: toIso(Math.min(pendingStart, now)),
      end_time: toIso(Math.max(pendingStart, now)),
    };
    markupsHistory.edit(
      (prev) => resolveOverlaps(prev, committed, makeId),
      "segment_create",
      {
        class_id: pending.class?.id ?? null,
        duration_ms: Math.abs(now - pendingStart),
        // Закрыт хоткеем другого класса — значит, сразу начат следующий.
        chained: mClass.id !== pending.class.id,
      }
    );

    if (mClass.id === pending.class.id) {
      // Тот же класс — просто применяем, цепочка не продолжается.
      setNewSegment(null);
    } else {
      // Другой класс — начало нового совпадает с концом закрытого.
      setNewSegment({ start_time: toIso(now), class: mClass, id: makeId() });
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleSectionCreation);
    return () => {
      document.removeEventListener('keydown', handleSectionCreation);
    };
  }, [])

  // Пробел — старт/стоп самого плеера. Маркер при этом ведёт видео через
  // onProgress, никаких перемоток на каждый кадр не возникает.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.code !== "Space") return;
      // Не мешаем вводу: поиск в селектах, поля настроек, хоткеи классов.
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      if (document.activeElement?.isContentEditable) return;

      // Гасим прокрутку страницы и «нажатие» сфокусированной кнопки.
      event.preventDefault();
      telemetry.track("play_toggle");
      playerRef.current?.togglePlay?.();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [])

  // Ctrl/⌘+Z — отмена, Ctrl/⌘+Shift+Z — повтор.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (!isUndoRedoShortcut(event)) return;
      // В полях ввода работает их собственная отмена набранного текста.
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      if (document.activeElement?.isContentEditable) return;

      event.preventDefault();
      if (event.shiftKey) markupsHistory.redo();
      else markupsHistory.undo();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [markupsHistory])

  // Пока сохранение идёт, второе не отправляем. Два сохранения подряд (второе
  // нажато, пока первое ещё шло) записывали в базу обе версии разметки — каждый
  // сегмент дважды (архив 30143). Ref, а не состояние: двойной клик успевает
  // раньше перерисовки.
  const savingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  const saveMarkups = async () => {
    if (!selectedArchiveIdRef.current || savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `/api/imu-archives/${selectedArchiveIdRef.current}/markup/`,
        {
          markups: markups,
          dataset_split: datasetSplit,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Сохранённое становится новой точкой отсчёта, иначе эффект черновиков
      // тут же запишет его обратно как несохранённое изменение.
      setInitialMarkups(markupsRef.current);
      localStorage.removeItem(draftStorageKey(selectedArchiveIdRef.current));
      telemetry.track("save_ok", { segments: markups.length });
      toast.success("Разметка успешно сохранена!");
    } catch (err) {
      telemetry.track("save_error", { status: err?.response?.status ?? null });
      toast.error("Ошибка сохранения разметки");
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }

  const loadPredictions = async () => {
    if (!selectedArchiveIdRef.current) return;
    // Границы цепочки берём из данных сенсоров, поэтому без них нельзя:
    // кнопка появляется по выбору архива, а CSV догружается позже.
    if (!sensorData?.minTimeMs || !sensorData?.maxTimeMs) {
      toast.error("Дождитесь загрузки данных архива");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const result = await axios.get(
        `/api/imu-archives/predict/${selectedArchiveIdRef.current}/`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Порядок от API не гарантирован, а цепочка целиком на него опирается:
      // из несортированного списка получились бы вывернутые сегменты
      // (end_time < start_time), а их mergeAdjacentSameClass не отсеивает.
      const segments = [...(result.data.segments || [])].sort(
        (a, b) => toMs(a.start_time) - toMs(b.start_time)
      );

      // end_time от модели отбрасываем: он не дотягивается до начала
      // следующего сегмента на шаг сетки дискретизации. Правый край берём
      // из start_time соседа — тогда стык точный и склейка однородных
      // сегментов срабатывает. array здесь — исходный массив, а не результат
      // маппинга, то есть читаем сырое значение соседа.
      // Предсказание заменяет всю разметку — правка, которую можно отменить.
      markupsHistory.edit(
        mergeAdjacentSameClass(segments.map((element, index, array) => {
          return {
            ...element,
            id: newIdRef.current + index,
            start_time: index == 0 ? toIso(sensorData.minTimeMs) : toIso(toMs(element.start_time)),
            end_time: index == array.length - 1 ? toIso(sensorData.maxTimeMs) : toIso(toMs(array[index+1].start_time)) ,
          }
        })),
        "predictions_load",
        { segments: segments.length, replaced_segments: markups.length }
      );
      newIdRef.current += segments.length;
      toast.success("Разметка успешно предсказана!");
    } catch (err) {
      toast.error("Ошибка предсказания разметки");
    };
  }

  useEffect(() => {
    console.log(clientSettings?.hideToast)
    if (clientSettings?.hideToast) {
      // Находим сам контейнер react-toastify в DOM и прячем его
      console.log("toast hidden")
      const container = document.querySelector('.Toastify');
      console.log(container)
      if (container) {
        container.style.display = 'none';
      }

      // При уходе со страницы возвращаем видимость назад
      return () => {
        if (container) container.style.display = 'block';
      };
    }
  }, [clientSettings]);

 return (
    <div className="bg-light vh-100 d-flex flex-column p-2 overflow-hidden" style={{ boxSizing: "border-box" }}>
      {/* ХЕДЕР */}
      <div className="d-flex justify-content-between align-items-center mb-1 px-2 flex-shrink-0">
        <div className="d-flex align-items-center gap-3">
          <CButton color="secondary" size="sm" as={Link} to="/dashboard">
            <CIcon icon={cilArrowLeft} className="me-1" /> Назад
          </CButton>
          <span className="fw-bold fs-6">Разметка IMU и Видео</span>
          {selectedArchiveId && (
            <span className="text-muted small">
              архив № {selectedArchiveId}
              {videoOffsetMs !== 0 && (
                <span className="badge bg-warning text-dark ms-2" title="Сдвиг видео относительно IMU">
                  видео {videoOffsetMs > 0 ? "+" : ""}{(videoOffsetMs / 1000).toFixed(1)} с
                </span>
              )}
            </span>
          )}
        </div>
        <div>
          {selectedArchiveId && (
            <CButton color="success" size="sm" className="text-white fw-bold px-3 shadow-sm" onClick={saveMarkups} disabled={isSaving}>
              {isSaving ? <><CSpinner size="sm" className="me-1" /> Сохранение…</> : "💾 Сохранить разметку"}
            </CButton>
          )}
          {selectedArchiveId && (
            <CButton style={{marginLeft: "1rem"}} color="warning" size="sm" className="text-white fw-bold px-3 shadow-sm" onClick={loadPredictions}>
              Предсказать разметку, удалив текущую
            </CButton>
          )}
          <CButton
            style={{ marginLeft: "1rem" }}
            color="secondary"
            variant="outline"
            size="sm"
            className="fw-bold px-3 shadow-sm"
            onClick={() => setSettingsModalVisible(true)}
            title="Настройки клиента"
          >
            ⚙ Настройки
          </CButton>
        </div>
      </div>

      {/* РАБОЧАЯ ОБЛАСТЬ: сверху видео | таймлайн, снизу панель выбора на всю ширину.
          minHeight: 0 у flex-потомков обязателен — иначе колонка не даёт им
          сжиматься, и верхний ряд выдавливает панель выбора за край экрана. */}
      <div className="d-flex flex-column flex-grow-1 gap-2 overflow-hidden" style={{ minHeight: 0 }}>

        {/* ВЕРХ: ВИДЕО | ТАЙМЛАЙН */}
        <div className="d-flex flex-grow-1 gap-2 overflow-hidden" style={{ minHeight: 0 }}>

          {/* ВИДЕО */}
          <div
            className="d-flex flex-column bg-dark rounded shadow-sm overflow-hidden flex-shrink-0"
            style={{ width: "36%", minWidth: "380px" }}
          >
            {selectedVideoUrl ? (
              <CustomVideoPlayer
                ref={playerRef}
                url={selectedVideoUrl}
                initialSeekTime={seekToTime}
                seekTriggerId={seekTriggerId}
                onProgress={handleProgress}
              />
            ) : (
              <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted p-3 text-center">
                {selectedArchiveId ? "Видеофайл не найден для этого архива" : "Выберите экскаватор и архив для загрузки"}
              </div>
            )}
          </div>

          {/* ТАЙМЛАЙН */}
          <CCard className="flex-grow-1 shadow-sm border-0 d-flex flex-column overflow-hidden" style={{ minWidth: 0 }}>
            <CCardHeader className="py-1 px-3 bg-white d-flex justify-content-between align-items-center border-bottom small flex-shrink-0">
              <span className="fw-bold">Таймлайн и Сенсоры IMU</span>
              <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                Колесо мыши: Зум | 2x Клик: Перемотка видео | Del: Удалить сегмент | Ctrl/⌘+Z: Отмена, +Shift: Повтор
              </span>
            </CCardHeader>

            <CCardBody className="p-2 d-flex flex-column flex-grow-1 overflow-y-auto" style={{ backgroundColor: "#f4f6f8" }}>
              {selectedArchiveId && sensorData ? (
                <NnMarkupTimeline
                  markups={markups}
                  markupsHistory={markupsHistory}
                  aiSessions={aiSessions}
                  data={sensorData}
                  newSegment={newSegment}
                  videoTimeMs={currentAbsoluteTimeMs}
                  setVideoTimeMs={seekToAbsoluteTime}
                  selectedClass={selectedClass}
                  setSelectedClass={setSelectedClass}
                  timezoneOffset={timezoneOffset}
                  makeId={makeId}
                  clientSettings={clientSettings}
                />
              ) : (
                <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                  <h5>{isLoading ? <CSpinner size="sm" className="me-2" /> : null} Выберите экскаватор и архив внизу</h5>
                </div>
              )}
            </CCardBody>
          </CCard>
        </div>

        {/* НИЗ: ПАНЕЛЬ ВЫБОРА НА ВСЮ ШИРИНУ — КЛАССЫ И СЕЛЕКТОРЫ ПО ПОРЯДКУ */}
        <CCard className="flex-shrink-0 shadow-sm border-0">
          <CCardBody className="p-2 d-flex flex-column gap-2 bg-white">

            {/* Классы разметки */}
            <div className="d-flex align-items-center gap-2 overflow-x-auto pb-1 border-bottom">
              <span className="text-muted fw-bold flex-shrink-0" style={{ fontSize: "0.72rem" }}>
                КЛАССЫ:
              </span>
              {availableClasses.length > 0 ? (
                <NnMarkupClasses
                  classes={availableClasses}
                  setClasses={setAvailableClasses}
                  selectedClass={selectedClass}
                  setSelectedClass={setSelectedClass}
                  hotkeys={hotkeys}
                  setHotkeys={setHotkeys}
                  vehicleType={selectedVehicleType}
                />
              ) : (
                <span className="text-muted small">Классы загрузятся после выбора архива</span>
              )}
            </div>

            {/* Организация -> 1.Техника -> 2.Архив -> 3.Тип -> 4.Сплит. */}
            <div className="d-flex align-items-end gap-2">

              {/* Организация — необязательный фильтр техники. Скрыт, если у
                  устройств нет organization_name: тогда фильтровать нечем. */}
              {uniqueOrganizations.length > 0 && (
                <div style={{ flex: 1.2, minWidth: 0 }}>
                  <label className="d-block text-muted fw-bold" style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
                    Организация
                  </label>
                  <SearchableSelect
                    size="sm"
                    value={orgFilter}
                    onChange={handleOrgChange}
                    placeholder="-- Все организации --"
                    options={organizationOptions}
                  />
                </div>
              )}

              {/* 1. Техника */}
              <div style={{ flex: 1.4, minWidth: 0 }}>
                <label className="d-block text-muted fw-bold" style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
                  1. Техника
                </label>
                <SearchableSelect
                  size="sm"
                  value={selectedDeviceId || ""}
                  onChange={(val) => handleDeviceChange({ target: { value: val } })}
                  placeholder="-- Выбрать --"
                  options={deviceOptions}
                />
              </div>

              {/* 2. Архив IMU */}
              <div style={{ flex: 2.2, minWidth: 0 }}>
                <label className="d-block text-muted fw-bold" style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
                  2. Архив
                </label>
                <SearchableSelect
                  size="sm"
                  value={selectedArchiveId || ""}
                  onChange={(val) => handleArchiveChange({ target: { value: val } })}
                  disabled={isLoading}
                  placeholder="-- Выберите архив или введите его № --"
                  freeOption={(q) => (/^\d+$/.test(q) ? { text: `Открыть архив № ${q}` } : null)}
                  onFreeSelect={openArchiveById}
                  options={imuArchives.map((a) => ({
                    value: a.id,
                    text: `${a.has_markup ? "✅ " : ""}${dayjs(a.timestamp).format("DD.MM HH:mm")} (${a.folder_path}) · № ${a.id}`,
                  }))}
                />
              </div>

              {/* 3. Тип техники. Кнопка сохранения — только при открытом архиве,
                  как и раньше; flex-shrink-0, чтобы поле w-100 её не выдавило. */}
              <div style={{ flex: 1.4, minWidth: 0 }}>
                <label className="d-block text-muted fw-bold" style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
                  3. Тип техники
                </label>
                <div className="d-flex align-items-center gap-1">
                  <SearchableSelect
                    size="sm"
                    value={selectedVehicleType || ""}
                    onChange={setSelectedVehicleType}
                    placeholder="Выберите тип транспорта"
                    options={vehicleTypeOptions}
                  />
                  {selectedArchiveId && (
                    <CButton size="sm" color="primary" variant="outline" className="flex-shrink-0" onClick={handleVehicleTypeChange} title="Сохранить тип">
                      💾
                    </CButton>
                  )}
                </div>
              </div>

              {/* 4. Сплит датасета */}
              <div className="flex-shrink-0">
                <label className="d-block text-muted fw-bold" style={{ fontSize: "0.7rem", marginBottom: "2px" }}>
                  4. Сплит
                </label>
                <div className="d-flex align-items-center gap-1">
                  <CFormSelect size="sm" style={{ width: "95px" }} value={datasetSplit || ""} onChange={(e) => setDatasetSplit(e.target.value)}>
                    <option value="null">--</option>
                    <option value="train">Train</option>
                    <option value="valid">Valid</option>
                  </CFormSelect>
                  {selectedArchiveId && (
                    <CButton size="sm" color="primary" variant="outline" className="flex-shrink-0" onClick={handleSplitChange} title="Сохранить сплит">
                      💾
                    </CButton>
                  )}
                </div>
              </div>

              {/* 5. Сдвиг видео относительно IMU */}
              <div className="flex-shrink-0">
                <label
                  className="d-block text-muted fw-bold"
                  style={{ fontSize: "0.7rem", marginBottom: "2px" }}
                  title="Время кадров видео сдвигается на это значение. Если на видео событие происходит раньше, чем на графиках IMU, — увеличьте сдвиг. Запоминается у архива; новый архив этой техники начинает с последнего сдвига."
                >
                  5. Сдвиг видео, с
                </label>
                <div className="d-flex align-items-center gap-1">
                  {[-1, -0.1].map((step) => (
                    <CButton
                      key={step}
                      size="sm"
                      color="secondary"
                      variant="outline"
                      className="flex-shrink-0 px-1"
                      disabled={!selectedArchiveId}
                      onClick={() => changeVideoOffset(videoOffsetMs + step * 1000)}
                    >
                      {step}
                    </CButton>
                  ))}
                  <CFormInput
                    size="sm"
                    type="number"
                    step="0.1"
                    style={{ width: "72px" }}
                    disabled={!selectedArchiveId}
                    value={offsetInput}
                    onChange={(e) => setOffsetInput(e.target.value)}
                    onBlur={() => changeVideoOffset(parseFloat(offsetInput) * 1000)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") changeVideoOffset(parseFloat(offsetInput) * 1000);
                    }}
                  />
                  {[0.1, 1].map((step) => (
                    <CButton
                      key={step}
                      size="sm"
                      color="secondary"
                      variant="outline"
                      className="flex-shrink-0 px-1"
                      disabled={!selectedArchiveId}
                      onClick={() => changeVideoOffset(videoOffsetMs + step * 1000)}
                    >
                      +{step}
                    </CButton>
                  ))}
                  <CButton
                    size="sm"
                    color="secondary"
                    variant="outline"
                    className="flex-shrink-0 px-1"
                    disabled={!selectedArchiveId || videoOffsetMs === 0}
                    onClick={() => changeVideoOffset(0)}
                    title="Без сдвига"
                  >
                    0
                  </CButton>
                </div>
              </div>

            </div>

          </CCardBody>
        </CCard>
      </div>

      {/* МОДАЛКА ЧЕРНОВИКА */}
      <CModal visible={draftModalVisible} onClose={() => setDraftModalVisible(false)}>
        <CModalHeader>
          <CModalTitle>Восстановить черновик?</CModalTitle>
        </CModalHeader>
        <CModalBody>Обнаружены несохраненные данные предыдущей сессии разметки.</CModalBody>
        <CModalFooter>
          <CButton color="secondary" size="sm" onClick={() => {
            localStorage.removeItem(draftStorageKey(selectedArchiveIdRef.current));
            telemetry.track("draft_discard", { segments: pendingDraftRef.current?.length ?? 0 });
            pendingDraftRef.current = null;
            setDraftModalVisible(false);
          }}>
            Удалить черновик
          </CButton>
          <CButton color="primary" size="sm" onClick={() => {
            // Берём то, что отложили при загрузке: перечитывать localStorage
            // уже нельзя — эффект черновиков мог перезаписать ключ.
            // Правка, а не загрузка: отмена вернёт серверную версию.
            if (pendingDraftRef.current) {
              markupsHistory.edit(pendingDraftRef.current, "draft_restore", {
                segments: pendingDraftRef.current.length,
              });
            }
            pendingDraftRef.current = null;
            setDraftModalVisible(false);
          }}>
            Восстановить
          </CButton>
        </CModalFooter>
      </CModal>

      {/* НАСТРОЙКИ КЛИЕНТА */}
      <CModal visible={settingsModalVisible} onClose={() => setSettingsModalVisible(false)}>
        <CModalHeader>
          <CModalTitle>Настройки клиента</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <div className="mb-3">
            <label className="form-label fw-bold" style={{ fontSize: "0.85rem" }}>
              Разрешение canvas
            </label>
            <CFormInput
              type="number"
              min="0.5"
              max="4"
              step="0.1"
              value={clientSettings.canvasResolutionDivider}
              onChange={(e) =>
                updateClientSettings({ canvasResolutionDivider: e.target.value })
              }
              onBlur={(e) =>
                updateClientSettings({
                  canvasResolutionDivider: clampResolutionDivider(e.target.value),
                })
              }
            />
            <div className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
              Делитель: 1.0 — как есть, 0.5 — вдвое чётче и тяжелее, 4.0 — мягче и легче
              для слабых машин. Допустимо от 0.5 до 4.0.
            </div>
          </div>

          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="hide-toast"
              checked={!!clientSettings.hideToast}
              onChange={(e) => updateClientSettings({ hideToast: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="hide-toast" style={{ fontSize: "0.85rem" }}>
              Спрятать баннеры уведомлений
            </label>
          </div>

          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="restore-playhead"
              checked={!!clientSettings.restorePlayheadAfterResize}
              onChange={(e) => updateClientSettings({ restorePlayheadAfterResize: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="restore-playhead" style={{ fontSize: "0.85rem" }}>
              Возвращать маркер времени после правки границы
            </label>
            <div className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
              Во время перетаскивания маркер идёт по движущейся границе. Если галочка
              снята, он там и останется.
            </div>
          </div>

          {/* Куда сходятся соседи при удалении сегмента между ними.
              Радиогруппа: активен ровно один вариант. */}
          <div className="mt-3">
            <label className="form-label fw-bold" style={{ fontSize: "0.85rem" }}>
              Схлопывание соседей при удалении
            </label>
            {(() => {
              // Все четыре варианта — одна радиогруппа, активен ровно один.
              // «Не схлопывать» отделён пробелом: он не про направление,
              // а про отказ от схлопывания вообще.
              const active = clientSettings.deleteSnapMode || "middle";
              const renderButton = ({ mode, glyph, title }) => (
                <button
                  key={mode}
                  type="button"
                  className={"btn btn-sm " + (active === mode ? "btn-primary" : "btn-outline-secondary")}
                  aria-pressed={active === mode}
                  title={title}
                  onClick={() => updateClientSettings({ deleteSnapMode: mode })}
                  style={{ lineHeight: 1, padding: "0.2rem 0.7rem" }}
                >
                  {glyph}
                </button>
              );

              return (
                <div className="d-flex align-items-center gap-3">
                  <div className="btn-group btn-group-sm" role="group" aria-label="Направление схлопывания">
                    {[
                      { mode: "left", glyph: "◀", title: "Начало правого соседа — к концу левого" },
                      { mode: "middle", glyph: "■", title: "Оба соседа — в середину удалённого" },
                      { mode: "right", glyph: "▶", title: "Конец левого соседа — к началу правого" },
                    ].map(renderButton)}
                  </div>
                  {renderButton({
                    mode: "none",
                    glyph: "⊘",
                    title: "Не схлопывать — оставить пробел на месте удалённого",
                  })}
                </div>
              );
            })()}
            <div className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
              Удаляем сегмент между двумя соседями: ◀ — граница встаёт в начало
              удалённого, ■ — в середину, ▶ — в конец, ⊘ — соседи не двигаются
              и на месте удалённого остаётся пробел. У края цепочки, где сосед
              только с одной стороны, ничего не сдвигается в любом случае.
            </div>
          </div>

        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" size="sm" onClick={() => updateClientSettings(DEFAULT_CLIENT_SETTINGS)}>
            По умолчанию
          </CButton>
          <CButton color="primary" size="sm" onClick={() => setSettingsModalVisible(false)}>
            Закрыть
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
};

export default ImuViewerPage;